/**
 * SQLite-based EventStore implementation
 * Primary store for hooks - WAL mode enables concurrent access
 */

import { randomUUID } from 'crypto';
import {
  MemoryEvent,
  MemoryEventInput,
  Session,
  AppendResult,
  OutboxItem,
  OutboxStats,
  OutboxStatsOptions,
  OutboxRecoveryOptions,
  OutboxRecoveryResult,
  ProjectScopeRepairOptions,
  ProjectScopeRepairResult,
  ProjectScopeRepairSample
} from './types.js';
import { makeCanonicalKey, makeDedupeKey } from './canonical-key.js';
import { generateCitationId } from './citation-generator.js';
import * as nodePath from 'path';
import { hashProjectPath } from './registry/project-path.js';
import {
  createSQLiteDatabase,
  sqliteRun,
  sqliteAll,
  sqliteGet,
  sqliteClose,
  sqliteExec,
  toDateFromSQLite,
  toSQLiteTimestamp,
  type SQLiteDatabase,
  type SQLiteOptions
} from './sqlite-wrapper.js';
import { MarkdownMirror } from './markdown-mirror.js';
import { VectorOutbox, type OutboxConfig } from './vector-outbox.js';
import { normalizeRetrievalDebugLanes, type RetrievalDebugLane } from './retrieval-debug-lanes.js';
import { computeMemoryUsageEvidence, type EvidenceMatch } from './usefulness-evidence.js';

export interface SQLiteEventStoreOptions extends SQLiteOptions {
  markdownMirrorRoot?: string;
  vectorOutbox?: false | VectorOutbox | Partial<OutboxConfig>;
}

type QueryRewriteKind = 'none' | 'follow-up-context' | 'intent-rewrite';

type RetrievalTraceDetailRecord = {
  eventId: string;
  score: number;
  semanticScore?: number;
  lexicalScore?: number;
  recencyScore?: number;
  lanes?: RetrievalDebugLane[];
};

function normalizeRetrievalTraceDetails(details?: RetrievalTraceDetailRecord[]): RetrievalTraceDetailRecord[] {
  return (details || []).map((detail) => {
    const lanes = normalizeRetrievalDebugLanes((detail as { lanes?: unknown }).lanes);
    const normalized: RetrievalTraceDetailRecord = {
      eventId: detail.eventId,
      score: detail.score
    };
    if (detail.semanticScore !== undefined) normalized.semanticScore = detail.semanticScore;
    if (detail.lexicalScore !== undefined) normalized.lexicalScore = detail.lexicalScore;
    if (detail.recencyScore !== undefined) normalized.recencyScore = detail.recencyScore;
    if (lanes.length > 0) normalized.lanes = lanes;
    return normalized;
  });
}

function parseRetrievalTraceDetails(value: unknown): RetrievalTraceDetailRecord[] {
  if (typeof value !== 'string' || value.length === 0) return [];
  const parsed = JSON.parse(value);
  return Array.isArray(parsed) ? normalizeRetrievalTraceDetails(parsed as RetrievalTraceDetailRecord[]) : [];
}

function normalizeQueryRewriteKind(value?: string | null): QueryRewriteKind {
  const normalized = (value || '').trim().toLowerCase();
  if (normalized === 'follow-up-context' || normalized === 'intent-rewrite') return normalized;
  return 'none';
}

const REWRITTEN_QUERY_REWRITE_KIND_SQL = `LOWER(TRIM(COALESCE(query_rewrite_kind, 'none'))) IN ('follow-up-context', 'intent-rewrite')`;
const DEFAULT_OUTBOX_STUCK_THRESHOLD_MS = 5 * 60 * 1000;
const DEFAULT_OUTBOX_MAX_RETRIES = 3;
// Bump when introducing an ordered migration that must run exactly once; gate
// such migrations on the persisted PRAGMA user_version.
const SQLITE_SCHEMA_VERSION = 1;

function emptyOutboxRecoveryResult(): OutboxRecoveryResult {
  return {
    embedding: { recoveredProcessing: 0, retriedFailed: 0 },
    vector: { recoveredProcessing: 0, retriedFailed: 0 }
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getNestedRecord(root: Record<string, unknown> | undefined, path: string[]): Record<string, unknown> | undefined {
  let cursor: unknown = root;
  for (const key of path) {
    if (!isRecord(cursor)) return undefined;
    cursor = cursor[key];
  }
  return isRecord(cursor) ? cursor : undefined;
}

function getNestedString(root: Record<string, unknown> | undefined, path: string[]): string | undefined {
  let cursor: unknown = root;
  for (const key of path) {
    if (!isRecord(cursor)) return undefined;
    cursor = cursor[key];
  }
  return typeof cursor === 'string' && cursor.length > 0 ? cursor : undefined;
}

function metadataProjectHash(metadata: Record<string, unknown> | undefined): string | undefined {
  return getNestedString(metadata, ['scope', 'project', 'hash']);
}

function metadataProjectPaths(metadata: Record<string, unknown> | undefined): string[] {
  const candidates = [
    getNestedString(metadata, ['projectPath']),
    getNestedString(metadata, ['sourceProjectPath']),
    getNestedString(metadata, ['scope', 'project', 'path'])
  ];
  const paths: string[] = [];
  for (const value of candidates) {
    if (value && !paths.includes(value)) paths.push(value);
  }
  return paths;
}

function metadataProjectPath(metadata: Record<string, unknown> | undefined): string | undefined {
  return metadataProjectPaths(metadata)[0];
}

function isActiveQuarantinedMetadata(metadata: Record<string, unknown> | undefined): boolean {
  const quarantine = getNestedRecord(metadata, ['quarantine']);
  return quarantine?.status === 'active';
}

function activeQuarantineStatusExpression(column = 'metadata'): string {
  return `COALESCE(json_extract(CASE WHEN json_valid(${column}) THEN ${column} ELSE '{}' END, '$.quarantine.status'), '')`;
}

function notActiveQuarantinedSql(column = 'metadata'): string {
  return `${activeQuarantineStatusExpression(column)} != 'active'`;
}

interface QuarantineReadOptions {
  includeQuarantined?: boolean;
}

function maybeQuarantinePredicate(options?: QuarantineReadOptions, column = 'metadata'): string {
  return options?.includeQuarantined ? '1=1' : notActiveQuarantinedSql(column);
}

function safeParseMetadataValue(value: unknown): Record<string, unknown> | undefined {
  if (!value) return undefined;
  if (typeof value === 'object') return isRecord(value) ? value : undefined;
  if (typeof value !== 'string') return undefined;
  try {
    const parsed = JSON.parse(value);
    return isRecord(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
}

function isImportedOrLegacyScopedMetadata(metadata: Record<string, unknown> | undefined): boolean {
  if (!metadata) return false;
  return Boolean(
    metadata.importedFrom
    || metadata.sourceSessionId
    || metadata.sourceSessionHash
    || metadata.hermesSource
    || metadata.projectPath
    || metadata.sourceProjectPath
    || metadata.source === 'hermes'
    || metadata.source === 'claude'
    || metadata.source === 'codex'
  );
}

function addMetadataTag(metadata: Record<string, unknown>, tag: string): void {
  const current = Array.isArray(metadata.tags)
    ? metadata.tags.filter((value): value is string => typeof value === 'string')
    : [];
  if (!current.includes(tag)) metadata.tags = [...current, tag];
}

function buildRepairResult(projectHash: string, dryRun: boolean): ProjectScopeRepairResult {
  return {
    dryRun,
    projectHash,
    scanned: 0,
    repaired: 0,
    quarantined: 0,
    alreadyScoped: 0,
    skipped: 0,
    samples: []
  };
}

function normalizeRepoName(value: string): string {
  return value.replace(/\.git$/i, '').trim().toLowerCase();
}

function projectBasename(projectPath?: string): string | undefined {
  if (!projectPath) return undefined;
  const trimmed = projectPath.replace(/[\\/]+$/, '');
  const basename = nodePath.basename(trimmed);
  return basename ? normalizeRepoName(basename) : undefined;
}

function isProjectScopeRepairExplanation(content: string): boolean {
  const normalized = content.toLowerCase();
  const hasRepairContext = /project[- ]scope|mis[- ]scoped|quarantine|contamination|legacy|오염|격리|repair/.test(normalized);
  const hasExplanationContext = /example|detector|trap|not a .*project task|기억|메모리|설명|수정|검증/.test(normalized);
  return hasRepairContext && hasExplanationContext;
}

function hasConflictingContentProjectHint(content: string, projectPath?: string): boolean {
  const currentName = projectBasename(projectPath);
  if (!currentName) return false;
  if (isProjectScopeRepairExplanation(content)) return false;

  const githubRepoPattern = /github\.com[:/]([^/\s`'"#)]+)\/([^/\s`'"#)]+)(?:\.git)?/gi;
  let githubMatch: RegExpExecArray | null;
  while ((githubMatch = githubRepoPattern.exec(content)) !== null) {
    const repo = normalizeRepoName(githubMatch[2] || '');
    if (repo && repo !== currentName) return true;
  }

  const workspacePathPattern = /\/workspace\/([^/\s`'"#)]+)/gi;
  let workspaceMatch: RegExpExecArray | null;
  while ((workspaceMatch = workspacePathPattern.exec(content)) !== null) {
    const repo = normalizeRepoName(workspaceMatch[1] || '');
    if (repo && repo !== currentName) return true;
  }

  return false;
}

export class SQLiteEventStore {
  private db: SQLiteDatabase;
  private initialized = false;
  private readonly readOnly: boolean;
  private readonly markdownMirror: MarkdownMirror | null;
  private readonly vectorOutbox: VectorOutbox | null;

  constructor(dbPath: string, options?: SQLiteEventStoreOptions) {
    this.readOnly = options?.readonly ?? false;
    this.db = createSQLiteDatabase(dbPath, {
      readonly: this.readOnly,
      walMode: !this.readOnly
    });
    this.markdownMirror = this.readOnly || !options?.markdownMirrorRoot
      ? null
      : new MarkdownMirror(options.markdownMirrorRoot);
    this.vectorOutbox = this.createVectorOutbox(options?.vectorOutbox);
  }

  private createVectorOutbox(option: SQLiteEventStoreOptions['vectorOutbox']): VectorOutbox | null {
    if (this.readOnly || option === false) return null;
    if (option instanceof VectorOutbox) return option;
    return new VectorOutbox(this.db, option ?? {});
  }

  private enqueueVectorOutboxEventSync(eventId: string): void {
    if (!this.vectorOutbox) return;
    this.vectorOutbox.enqueueSync('event', eventId);
  }

  private async enqueueVectorOutboxEvent(eventId: string): Promise<void> {
    this.enqueueVectorOutboxEventSync(eventId);
  }

  /**
   * Initialize database schema
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    // In read-only mode, skip schema creation
    if (this.readOnly) {
      this.initialized = true;
      return;
    }

    // Create all tables in a single exec for efficiency
    sqliteExec(this.db, `
      -- L0 EventStore: Single Source of Truth (immutable, append-only)
      CREATE TABLE IF NOT EXISTS events (
        id TEXT PRIMARY KEY,
        event_type TEXT NOT NULL,
        session_id TEXT NOT NULL,
        timestamp TEXT NOT NULL,
        content TEXT NOT NULL,
        canonical_key TEXT NOT NULL,
        dedupe_key TEXT UNIQUE,
        metadata TEXT,
        access_count INTEGER DEFAULT 0,
        last_accessed_at TEXT
      );

      -- Dedup table for idempotency
      CREATE TABLE IF NOT EXISTS event_dedup (
        dedupe_key TEXT PRIMARY KEY,
        event_id TEXT NOT NULL,
        created_at TEXT DEFAULT (datetime('now'))
      );

      -- Session metadata
      CREATE TABLE IF NOT EXISTS sessions (
        id TEXT PRIMARY KEY,
        started_at TEXT NOT NULL,
        ended_at TEXT,
        project_path TEXT,
        summary TEXT,
        tags TEXT
      );

      -- Insights (derived data, rebuildable)
      CREATE TABLE IF NOT EXISTS insights (
        id TEXT PRIMARY KEY,
        insight_type TEXT NOT NULL,
        content TEXT NOT NULL,
        canonical_key TEXT NOT NULL,
        confidence REAL,
        source_events TEXT,
        created_at TEXT,
        last_updated TEXT
      );

      -- Embedding Outbox (Single-Writer Pattern)
      CREATE TABLE IF NOT EXISTS embedding_outbox (
        id TEXT PRIMARY KEY,
        event_id TEXT NOT NULL,
        content TEXT NOT NULL,
        status TEXT DEFAULT 'pending',
        retry_count INTEGER DEFAULT 0,
        created_at TEXT DEFAULT (datetime('now')),
        processed_at TEXT,
        error_message TEXT
      );

      -- Projection offset tracking
      CREATE TABLE IF NOT EXISTS projection_offsets (
        projection_name TEXT PRIMARY KEY,
        last_event_id TEXT,
        last_timestamp TEXT,
        updated_at TEXT DEFAULT (datetime('now'))
      );

      -- Memory level tracking
      CREATE TABLE IF NOT EXISTS memory_levels (
        event_id TEXT PRIMARY KEY,
        level TEXT NOT NULL DEFAULT 'L0',
        promoted_at TEXT DEFAULT (datetime('now'))
      );

      -- Entries (immutable memory units)
      CREATE TABLE IF NOT EXISTS entries (
        entry_id TEXT PRIMARY KEY,
        created_ts TEXT NOT NULL,
        entry_type TEXT NOT NULL,
        title TEXT NOT NULL,
        content_json TEXT NOT NULL,
        stage TEXT NOT NULL DEFAULT 'raw',
        status TEXT DEFAULT 'active',
        superseded_by TEXT,
        build_id TEXT,
        evidence_json TEXT,
        canonical_key TEXT,
        created_at TEXT DEFAULT (datetime('now'))
      );

      -- Entities (task/condition/artifact)
      CREATE TABLE IF NOT EXISTS entities (
        entity_id TEXT PRIMARY KEY,
        entity_type TEXT NOT NULL,
        canonical_key TEXT NOT NULL,
        title TEXT NOT NULL,
        stage TEXT NOT NULL DEFAULT 'raw',
        status TEXT NOT NULL DEFAULT 'active',
        current_json TEXT NOT NULL,
        title_norm TEXT,
        search_text TEXT,
        created_at TEXT DEFAULT (datetime('now')),
        updated_at TEXT DEFAULT (datetime('now'))
      );

      -- Entity aliases for canonical key lookup
      CREATE TABLE IF NOT EXISTS entity_aliases (
        entity_type TEXT NOT NULL,
        canonical_key TEXT NOT NULL,
        entity_id TEXT NOT NULL,
        is_primary INTEGER DEFAULT 0,
        created_at TEXT DEFAULT (datetime('now')),
        PRIMARY KEY(entity_type, canonical_key)
      );

      -- Edges (relationships between entries/entities)
      CREATE TABLE IF NOT EXISTS edges (
        edge_id TEXT PRIMARY KEY,
        src_type TEXT NOT NULL,
        src_id TEXT NOT NULL,
        rel_type TEXT NOT NULL,
        dst_type TEXT NOT NULL,
        dst_id TEXT NOT NULL,
        meta_json TEXT,
        created_at TEXT DEFAULT (datetime('now'))
      );

      -- Vector Outbox V2 Table
      CREATE TABLE IF NOT EXISTS vector_outbox (
        job_id TEXT PRIMARY KEY,
        item_kind TEXT NOT NULL,
        item_id TEXT NOT NULL,
        embedding_version TEXT NOT NULL,
        status TEXT NOT NULL DEFAULT 'pending',
        retry_count INTEGER DEFAULT 0,
        error TEXT,
        created_at TEXT DEFAULT (datetime('now')),
        updated_at TEXT DEFAULT (datetime('now')),
        UNIQUE(item_kind, item_id, embedding_version)
      );

      -- Build Runs
      CREATE TABLE IF NOT EXISTS build_runs (
        build_id TEXT PRIMARY KEY,
        started_at TEXT NOT NULL,
        finished_at TEXT,
        extractor_model TEXT NOT NULL,
        extractor_prompt_hash TEXT NOT NULL,
        embedder_model TEXT NOT NULL,
        embedding_version TEXT NOT NULL,
        idris_version TEXT NOT NULL,
        schema_version TEXT NOT NULL,
        status TEXT NOT NULL DEFAULT 'running',
        error TEXT
      );

      -- Pipeline Metrics
      CREATE TABLE IF NOT EXISTS pipeline_metrics (
        id TEXT PRIMARY KEY,
        ts TEXT NOT NULL,
        stage TEXT NOT NULL,
        latency_ms REAL NOT NULL,
        success INTEGER NOT NULL,
        error TEXT,
        session_id TEXT
      );

      -- Working Set table (active memory window)
      CREATE TABLE IF NOT EXISTS working_set (
        id TEXT PRIMARY KEY,
        event_id TEXT NOT NULL,
        added_at TEXT DEFAULT (datetime('now')),
        relevance_score REAL DEFAULT 1.0,
        topics TEXT,
        expires_at TEXT
      );

      -- Consolidated Memories table (long-term integrated memories)
      CREATE TABLE IF NOT EXISTS consolidated_memories (
        memory_id TEXT PRIMARY KEY,
        summary TEXT NOT NULL,
        topics TEXT,
        source_events TEXT,
        confidence REAL DEFAULT 0.5,
        created_at TEXT DEFAULT (datetime('now')),
        accessed_at TEXT,
        access_count INTEGER DEFAULT 0
      );

      -- Junction: consolidated memory -> source event id (indexed lookup that
      -- replaces per-event source_events LIKE scans during consolidation).
      CREATE TABLE IF NOT EXISTS consolidated_memory_events (
        memory_id TEXT NOT NULL,
        event_id TEXT NOT NULL,
        PRIMARY KEY (memory_id, event_id)
      );
      CREATE INDEX IF NOT EXISTS idx_cme_event ON consolidated_memory_events(event_id);

      -- Continuity Log table (tracks context transitions)
      CREATE TABLE IF NOT EXISTS continuity_log (
        log_id TEXT PRIMARY KEY,
        from_context_id TEXT,
        to_context_id TEXT,
        continuity_score REAL,
        transition_type TEXT,
        created_at TEXT DEFAULT (datetime('now'))
      );

      -- Consolidated Rules table (long-term stable memory)
      CREATE TABLE IF NOT EXISTS consolidated_rules (
        rule_id TEXT PRIMARY KEY,
        rule TEXT NOT NULL,
        topics TEXT,
        source_memory_ids TEXT,
        source_events TEXT,
        confidence REAL DEFAULT 0.5,
        created_at TEXT DEFAULT (datetime('now'))
      );

      -- Endless Mode Config table
      CREATE TABLE IF NOT EXISTS endless_config (
        key TEXT PRIMARY KEY,
        value TEXT,
        updated_at TEXT DEFAULT (datetime('now'))
      );

      -- Memory Helpfulness tracking
      CREATE TABLE IF NOT EXISTS memory_helpfulness (
        id TEXT PRIMARY KEY,
        event_id TEXT NOT NULL,
        session_id TEXT NOT NULL,
        retrieval_score REAL DEFAULT 0,
        query_preview TEXT,
        session_continued INTEGER DEFAULT 0,
        prompt_count_after INTEGER DEFAULT 0,
        tool_success_count INTEGER DEFAULT 0,
        tool_total_count INTEGER DEFAULT 0,
        was_reasked INTEGER DEFAULT 0,
        helpfulness_score REAL DEFAULT 0.5,
        created_at TEXT DEFAULT (datetime('now')),
        measured_at TEXT
      );

      -- Retrieval trace log (query -> candidates -> selected for context)
      CREATE TABLE IF NOT EXISTS retrieval_traces (
        trace_id TEXT PRIMARY KEY,
        session_id TEXT,
        project_hash TEXT,
        query_text TEXT NOT NULL,
        raw_query_text TEXT,
        query_rewrite_kind TEXT,
        strategy TEXT,
        candidate_event_ids TEXT,
        selected_event_ids TEXT,
        candidate_details_json TEXT,
        selected_details_json TEXT,
        candidate_count INTEGER DEFAULT 0,
        selected_count INTEGER DEFAULT 0,
        confidence TEXT,
        fallback_trace TEXT,
        created_at TEXT DEFAULT (datetime('now'))
      );

      -- Sync position tracking (for SQLite -> DuckDB sync)
      CREATE TABLE IF NOT EXISTS sync_positions (
        target_name TEXT PRIMARY KEY,
        last_event_id TEXT,
        last_timestamp TEXT,
        updated_at TEXT DEFAULT (datetime('now'))
      );

      -- Memory Operations: facet assignments (derived, rebuildable projection)
      CREATE TABLE IF NOT EXISTS memory_facets (
        id TEXT PRIMARY KEY,
        target_type TEXT NOT NULL,
        target_id TEXT NOT NULL,
        dimension TEXT NOT NULL,
        value TEXT NOT NULL,
        confidence REAL NOT NULL DEFAULT 1.0,
        source TEXT NOT NULL DEFAULT 'manual',
        evidence_event_ids TEXT NOT NULL DEFAULT '[]',
        project_hash TEXT NOT NULL DEFAULT '',
        created_at TEXT NOT NULL,
        updated_at TEXT NOT NULL,
        UNIQUE(target_type, target_id, dimension, value, source, project_hash)
      );

      -- Memory Operations: operational action projection
      CREATE TABLE IF NOT EXISTS memory_actions (
        action_id TEXT PRIMARY KEY,
        project_hash TEXT NOT NULL,
        title TEXT NOT NULL,
        status TEXT NOT NULL DEFAULT 'pending',
        priority INTEGER NOT NULL DEFAULT 0,
        source_event_ids TEXT NOT NULL DEFAULT '[]',
        related_entity_ids TEXT NOT NULL DEFAULT '[]',
        current_checkpoint_id TEXT,
        lease_id TEXT,
        created_at TEXT NOT NULL,
        updated_at TEXT NOT NULL
      );

      -- Memory Operations: action dependency/reference edges
      CREATE TABLE IF NOT EXISTS memory_action_edges (
        edge_id TEXT PRIMARY KEY,
        src_action_id TEXT NOT NULL,
        rel_type TEXT NOT NULL,
        dst_type TEXT NOT NULL,
        dst_id TEXT NOT NULL,
        confidence REAL NOT NULL DEFAULT 1.0,
        source TEXT NOT NULL DEFAULT 'manual',
        created_at TEXT NOT NULL,
        UNIQUE(src_action_id, rel_type, dst_type, dst_id, source)
      );

      -- Memory Operations: short-lived leases for operational work
      CREATE TABLE IF NOT EXISTS memory_leases (
        lease_id TEXT PRIMARY KEY,
        target_type TEXT NOT NULL,
        target_id TEXT NOT NULL,
        holder TEXT NOT NULL,
        expires_at TEXT NOT NULL,
        metadata_json TEXT,
        created_at TEXT NOT NULL,
        renewed_at TEXT,
        released_at TEXT
      );

      -- Memory Operations: resumable checkpoints for delegated or long-running work
      CREATE TABLE IF NOT EXISTS memory_checkpoints (
        checkpoint_id TEXT PRIMARY KEY,
        project_hash TEXT NOT NULL,
        action_id TEXT,
        session_id TEXT,
        title TEXT NOT NULL,
        summary TEXT NOT NULL,
        state_json TEXT NOT NULL,
        source_event_ids TEXT NOT NULL DEFAULT '[]',
        created_at TEXT NOT NULL,
        expires_at TEXT
      );

      -- Memory Operations: retention lifecycle score projection
      CREATE TABLE IF NOT EXISTS memory_retention_scores (
        score_id TEXT PRIMARY KEY,
        target_type TEXT NOT NULL,
        target_id TEXT NOT NULL,
        project_hash TEXT NOT NULL,
        policy_version TEXT NOT NULL,
        decision TEXT NOT NULL,
        lifecycle_score REAL NOT NULL,
        factors_json TEXT NOT NULL,
        reasons_json TEXT NOT NULL,
        dry_run_diff_json TEXT NOT NULL,
        source_event_ids TEXT NOT NULL DEFAULT '[]',
        evaluated_at TEXT NOT NULL,
        created_at TEXT NOT NULL,
        updated_at TEXT NOT NULL,
        UNIQUE(target_type, target_id, project_hash, policy_version)
      );

      -- Memory Operations: procedural lessons derived from successful workflows
      CREATE TABLE IF NOT EXISTS memory_lessons (
        lesson_id TEXT PRIMARY KEY,
        project_hash TEXT NOT NULL DEFAULT '',
        name TEXT NOT NULL,
        trigger TEXT NOT NULL,
        steps_json TEXT NOT NULL,
        confidence REAL NOT NULL,
        source_session_ids TEXT NOT NULL DEFAULT '[]',
        source_event_ids TEXT NOT NULL DEFAULT '[]',
        failure_modes_json TEXT NOT NULL DEFAULT '[]',
        skill_candidate INTEGER NOT NULL DEFAULT 0,
        created_at TEXT NOT NULL,
        updated_at TEXT NOT NULL,
        UNIQUE(project_hash, name)
      );

      -- Perspective Memory: privacy-safe actors/peers
      CREATE TABLE IF NOT EXISTS memory_actors (
        actor_id TEXT PRIMARY KEY,
        project_hash TEXT NOT NULL DEFAULT '',
        kind TEXT NOT NULL,
        display_name TEXT NOT NULL,
        source TEXT NOT NULL,
        metadata_json TEXT,
        created_at TEXT NOT NULL,
        updated_at TEXT NOT NULL
      );

      -- Perspective Memory: actors participating in a session and observation policy
      CREATE TABLE IF NOT EXISTS session_actors (
        project_hash TEXT NOT NULL DEFAULT '',
        session_id TEXT NOT NULL,
        actor_id TEXT NOT NULL,
        role_in_session TEXT NOT NULL,
        observe_self INTEGER NOT NULL DEFAULT 1,
        observe_others INTEGER NOT NULL DEFAULT 0,
        joined_at TEXT NOT NULL,
        left_at TEXT,
        metadata_json TEXT,
        PRIMARY KEY(project_hash, session_id, actor_id)
      );

      -- Perspective Memory: compact Honcho-style actor cards
      CREATE TABLE IF NOT EXISTS actor_cards (
        card_id TEXT PRIMARY KEY,
        project_hash TEXT NOT NULL DEFAULT '',
        observer_actor_id TEXT NOT NULL,
        observed_actor_id TEXT NOT NULL,
        entries_json TEXT NOT NULL,
        source_event_ids_json TEXT NOT NULL DEFAULT '[]',
        updated_by TEXT,
        created_at TEXT NOT NULL,
        updated_at TEXT NOT NULL,
        UNIQUE(project_hash, observer_actor_id, observed_actor_id)
      );

      -- Perspective Memory: observer -> observed claims with evidence pointers
      CREATE TABLE IF NOT EXISTS perspective_observations (
        observation_id TEXT PRIMARY KEY,
        project_hash TEXT NOT NULL DEFAULT '',
        observer_actor_id TEXT NOT NULL,
        observed_actor_id TEXT NOT NULL,
        session_id TEXT,
        level TEXT NOT NULL,
        content TEXT NOT NULL,
        confidence REAL NOT NULL,
        source_event_ids_json TEXT NOT NULL DEFAULT '[]',
        source_observation_ids_json TEXT NOT NULL DEFAULT '[]',
        created_by TEXT NOT NULL,
        metadata_json TEXT,
        content_hash TEXT NOT NULL,
        source_hash TEXT NOT NULL,
        created_at TEXT NOT NULL,
        updated_at TEXT NOT NULL,
        deleted_at TEXT,
        UNIQUE(project_hash, observer_actor_id, observed_actor_id, level, content_hash, source_hash)
      );

      -- Perspective Memory: FTS5 index for observation content queries.
      -- Use an external-content FTS table so the searchable projection stores the index,
      -- not a second raw copy of private observation text.
      CREATE VIRTUAL TABLE IF NOT EXISTS perspective_observations_fts USING fts5(
        content,
        observation_id UNINDEXED,
        content='perspective_observations',
        content_rowid='rowid',
        tokenize='porter unicode61'
      );

      -- Backfill the FTS table for writable legacy stores that predate the FTS projection.
      INSERT OR IGNORE INTO perspective_observations_fts(rowid, content, observation_id)
      SELECT rowid, content, observation_id FROM perspective_observations;

      CREATE TRIGGER IF NOT EXISTS perspective_observations_fts_insert AFTER INSERT ON perspective_observations BEGIN
        INSERT INTO perspective_observations_fts(rowid, content, observation_id) VALUES (NEW.rowid, NEW.content, NEW.observation_id);
      END;

      CREATE TRIGGER IF NOT EXISTS perspective_observations_fts_delete AFTER DELETE ON perspective_observations BEGIN
        INSERT INTO perspective_observations_fts(perspective_observations_fts, rowid, content, observation_id) VALUES('delete', OLD.rowid, OLD.content, OLD.observation_id);
      END;

      CREATE TRIGGER IF NOT EXISTS perspective_observations_fts_update AFTER UPDATE ON perspective_observations BEGIN
        INSERT INTO perspective_observations_fts(perspective_observations_fts, rowid, content, observation_id) VALUES('delete', OLD.rowid, OLD.content, OLD.observation_id);
        INSERT INTO perspective_observations_fts(rowid, content, observation_id) VALUES (NEW.rowid, NEW.content, NEW.observation_id);
      END;

      -- Memory Operations: governance/audit trail for state-changing operations
      CREATE TABLE IF NOT EXISTS memory_governance_audit (
        audit_id TEXT PRIMARY KEY,
        operation TEXT NOT NULL,
        actor TEXT NOT NULL,
        project_hash TEXT,
        target_type TEXT NOT NULL,
        target_id TEXT NOT NULL,
        before_json TEXT,
        after_json TEXT,
        source_event_ids TEXT NOT NULL DEFAULT '[]',
        created_at TEXT NOT NULL
      );

      -- Create indexes
      CREATE INDEX IF NOT EXISTS idx_events_session ON events(session_id);
      CREATE INDEX IF NOT EXISTS idx_events_timestamp ON events(timestamp);
      CREATE INDEX IF NOT EXISTS idx_entries_type ON entries(entry_type);
      CREATE INDEX IF NOT EXISTS idx_entries_stage ON entries(stage);
      CREATE INDEX IF NOT EXISTS idx_entries_canonical ON entries(canonical_key);
      CREATE INDEX IF NOT EXISTS idx_entities_type_key ON entities(entity_type, canonical_key);
      CREATE INDEX IF NOT EXISTS idx_entities_status ON entities(status);
      CREATE INDEX IF NOT EXISTS idx_edges_src ON edges(src_id, rel_type);
      CREATE INDEX IF NOT EXISTS idx_edges_dst ON edges(dst_id, rel_type);
      CREATE INDEX IF NOT EXISTS idx_edges_rel ON edges(rel_type);
      CREATE INDEX IF NOT EXISTS idx_outbox_status ON vector_outbox(status);
      CREATE INDEX IF NOT EXISTS idx_outbox_created ON vector_outbox(created_at);
      CREATE INDEX IF NOT EXISTS idx_working_set_expires ON working_set(expires_at);
      CREATE INDEX IF NOT EXISTS idx_working_set_relevance ON working_set(relevance_score);
      CREATE INDEX IF NOT EXISTS idx_consolidated_confidence ON consolidated_memories(confidence);
      CREATE INDEX IF NOT EXISTS idx_continuity_created ON continuity_log(created_at);
      CREATE INDEX IF NOT EXISTS idx_consolidated_rules_confidence ON consolidated_rules(confidence);
      CREATE INDEX IF NOT EXISTS idx_embedding_outbox_status ON embedding_outbox(status);
      CREATE INDEX IF NOT EXISTS idx_helpfulness_event ON memory_helpfulness(event_id);
      CREATE INDEX IF NOT EXISTS idx_helpfulness_session ON memory_helpfulness(session_id);
      CREATE INDEX IF NOT EXISTS idx_helpfulness_score ON memory_helpfulness(helpfulness_score DESC);
      CREATE INDEX IF NOT EXISTS idx_helpfulness_created_at ON memory_helpfulness(created_at);
      CREATE INDEX IF NOT EXISTS idx_helpfulness_measured_at ON memory_helpfulness(measured_at);
      CREATE INDEX IF NOT EXISTS idx_retrieval_traces_created_at ON retrieval_traces(created_at DESC);
      CREATE INDEX IF NOT EXISTS idx_retrieval_traces_project_hash ON retrieval_traces(project_hash);
      CREATE INDEX IF NOT EXISTS idx_retrieval_traces_session_id ON retrieval_traces(session_id);
      CREATE INDEX IF NOT EXISTS idx_memory_facets_project_dimension_value ON memory_facets(project_hash, dimension, value);
      CREATE INDEX IF NOT EXISTS idx_memory_facets_target ON memory_facets(target_type, target_id);
      CREATE INDEX IF NOT EXISTS idx_memory_facets_dimension_value_confidence ON memory_facets(dimension, value, confidence DESC);
      CREATE INDEX IF NOT EXISTS idx_memory_actions_project_status_priority ON memory_actions(project_hash, status, priority DESC, updated_at DESC);
      CREATE INDEX IF NOT EXISTS idx_memory_action_edges_src ON memory_action_edges(src_action_id, rel_type);
      CREATE INDEX IF NOT EXISTS idx_memory_action_edges_dst ON memory_action_edges(dst_type, dst_id);
      CREATE INDEX IF NOT EXISTS idx_memory_leases_target_expires ON memory_leases(target_type, target_id, expires_at);
      CREATE INDEX IF NOT EXISTS idx_memory_checkpoints_project_action_created ON memory_checkpoints(project_hash, action_id, created_at DESC);
      CREATE INDEX IF NOT EXISTS idx_memory_checkpoints_project_session_created ON memory_checkpoints(project_hash, session_id, created_at DESC);
      CREATE INDEX IF NOT EXISTS idx_memory_retention_scores_project_decision_score ON memory_retention_scores(project_hash, decision, lifecycle_score ASC, evaluated_at DESC);
      CREATE INDEX IF NOT EXISTS idx_memory_retention_scores_target ON memory_retention_scores(target_type, target_id, project_hash);
      CREATE INDEX IF NOT EXISTS idx_memory_retention_scores_policy_evaluated ON memory_retention_scores(policy_version, evaluated_at DESC);
      CREATE INDEX IF NOT EXISTS idx_memory_lessons_project_confidence ON memory_lessons(project_hash, confidence DESC, updated_at DESC);
      CREATE INDEX IF NOT EXISTS idx_memory_lessons_skill_candidate ON memory_lessons(project_hash, skill_candidate, confidence DESC);
      CREATE INDEX IF NOT EXISTS idx_memory_lessons_updated ON memory_lessons(updated_at DESC);
      CREATE INDEX IF NOT EXISTS idx_memory_actors_project_kind ON memory_actors(project_hash, kind, updated_at DESC);
      CREATE INDEX IF NOT EXISTS idx_memory_actors_source ON memory_actors(source, kind);
      CREATE INDEX IF NOT EXISTS idx_session_actors_session ON session_actors(project_hash, session_id, role_in_session);
      CREATE INDEX IF NOT EXISTS idx_session_actors_actor ON session_actors(actor_id, project_hash, session_id);
      CREATE INDEX IF NOT EXISTS idx_actor_cards_perspective ON actor_cards(project_hash, observer_actor_id, observed_actor_id);
      CREATE INDEX IF NOT EXISTS idx_actor_cards_updated ON actor_cards(updated_at DESC);
      CREATE INDEX IF NOT EXISTS idx_perspective_observations_perspective_level ON perspective_observations(project_hash, observer_actor_id, observed_actor_id, level, confidence DESC, updated_at DESC);
      CREATE INDEX IF NOT EXISTS idx_perspective_observations_session ON perspective_observations(project_hash, session_id, level, updated_at DESC);
      CREATE INDEX IF NOT EXISTS idx_perspective_observations_source_hash ON perspective_observations(project_hash, source_hash);
      CREATE INDEX IF NOT EXISTS idx_perspective_observations_deleted ON perspective_observations(deleted_at);
      CREATE INDEX IF NOT EXISTS idx_memory_governance_audit_project_operation ON memory_governance_audit(project_hash, operation, created_at DESC);
      CREATE INDEX IF NOT EXISTS idx_memory_governance_audit_target ON memory_governance_audit(target_type, target_id, created_at DESC);

      -- FTS5 Full-Text Search for fast keyword search
      CREATE VIRTUAL TABLE IF NOT EXISTS events_fts USING fts5(
        content,
        event_id UNINDEXED,
        tokenize='porter unicode61'
      );

      -- Triggers to keep FTS in sync with events table
      CREATE TRIGGER IF NOT EXISTS events_fts_insert AFTER INSERT ON events BEGIN
        INSERT INTO events_fts(rowid, content, event_id) VALUES (NEW.rowid, NEW.content, NEW.id);
      END;

      CREATE TRIGGER IF NOT EXISTS events_fts_delete AFTER DELETE ON events BEGIN
        DELETE FROM events_fts WHERE rowid = OLD.rowid;
      END;

      CREATE TRIGGER IF NOT EXISTS events_fts_update AFTER UPDATE ON events BEGIN
        DELETE FROM events_fts WHERE rowid = OLD.rowid;
        INSERT INTO events_fts(rowid, content, event_id) VALUES (NEW.rowid, NEW.content, NEW.id);
      END;

      -- Reverse index for citation ids (a non-reversible hash of event id), so
      -- citation lookups are O(1) instead of scanning recent events and hashing
      -- each one. Populated lazily/self-healing by getEventByCitationId.
      CREATE TABLE IF NOT EXISTS event_citations (
        event_id TEXT PRIMARY KEY,
        citation_id TEXT NOT NULL
      );
      CREATE INDEX IF NOT EXISTS idx_event_citations_citation ON event_citations(citation_id);
    `);


    // Best-effort forward migration for action edge source ownership
    this.addColumnIfMissing('memory_action_edges', 'source', `TEXT NOT NULL DEFAULT 'manual'`);
    try {
      const edgeIndexes = sqliteAll<{ name: string; unique: number }>(this.db, `PRAGMA index_list(memory_action_edges)`, []);
      const hasSourceAwareUnique = edgeIndexes.some((index) => {
        if (Number(index.unique) !== 1) return false;
        if (!/^[A-Za-z0-9_]+$/.test(index.name)) return false;
        const escapedName = index.name.replace(/"/g, '""');
        const columns = sqliteAll<{ name: string }>(this.db, 'PRAGMA index_info("' + escapedName + '")', [])
          .map((column) => column.name);
        return columns.length === 5
          && columns[0] === 'src_action_id'
          && columns[1] === 'rel_type'
          && columns[2] === 'dst_type'
          && columns[3] === 'dst_id'
          && columns[4] === 'source';
      });
      if (!hasSourceAwareUnique) {
        sqliteExec(this.db, `
          DROP TABLE IF EXISTS memory_action_edges_v2;
          CREATE TABLE memory_action_edges_v2 (
            edge_id TEXT PRIMARY KEY,
            src_action_id TEXT NOT NULL,
            rel_type TEXT NOT NULL,
            dst_type TEXT NOT NULL,
            dst_id TEXT NOT NULL,
            confidence REAL NOT NULL DEFAULT 1.0,
            source TEXT NOT NULL DEFAULT 'manual',
            created_at TEXT NOT NULL,
            UNIQUE(src_action_id, rel_type, dst_type, dst_id, source)
          );
          INSERT OR IGNORE INTO memory_action_edges_v2 (
            edge_id, src_action_id, rel_type, dst_type, dst_id, confidence, source, created_at
          )
          SELECT edge_id, src_action_id, rel_type, dst_type, dst_id, confidence, source, created_at
          FROM memory_action_edges;
          DROP TABLE memory_action_edges;
          ALTER TABLE memory_action_edges_v2 RENAME TO memory_action_edges;
          CREATE INDEX IF NOT EXISTS idx_memory_action_edges_src ON memory_action_edges(src_action_id, rel_type);
          CREATE INDEX IF NOT EXISTS idx_memory_action_edges_dst ON memory_action_edges(dst_type, dst_id);
        `);
      }
    } catch {
      // action edge table may not exist in partial migrations
    }

    // Best-effort forward migration for helpfulness evidence columns:
    // trace_id links each injected memory back to its retrieval_traces row
    // (question -> memory), content grounding measures whether later
    // assistant responses actually reused the memory's content.
    this.addColumnIfMissing('memory_helpfulness', 'trace_id', 'TEXT');
    this.addColumnIfMissing('memory_helpfulness', 'source', `TEXT DEFAULT 'user_prompt'`);
    this.addColumnIfMissing('memory_helpfulness', 'content_overlap_score', 'REAL');
    this.addColumnIfMissing('memory_helpfulness', 'evidence_json', 'TEXT');
    this.addColumnIfMissing('memory_helpfulness', 'injected_content', 'TEXT');
    try {
      sqliteExec(this.db, `CREATE INDEX IF NOT EXISTS idx_helpfulness_trace ON memory_helpfulness(trace_id);`);
    } catch {
      // index/table may not exist in partial migrations
    }

    // Best-effort forward migration for retrieval trace detail columns
    this.addColumnIfMissing('retrieval_traces', 'selected_details_json', 'TEXT');
    this.addColumnIfMissing('retrieval_traces', 'candidate_details_json', 'TEXT');
    this.addColumnIfMissing('retrieval_traces', 'raw_query_text', 'TEXT');
    this.addColumnIfMissing('retrieval_traces', 'query_rewrite_kind', 'TEXT');
    try {
      sqliteExec(this.db, `CREATE INDEX IF NOT EXISTS idx_retrieval_traces_query_rewrite_kind ON retrieval_traces(query_rewrite_kind);`);
    } catch {
      // index/table may not exist in partial migrations
    }

    // Forward-migrate the events table columns added over time. turn_id groups
    // events within a conversation turn; access_count/last_accessed_at back
    // access analytics.
    this.addColumnIfMissing('events', 'access_count', 'INTEGER DEFAULT 0');
    this.addColumnIfMissing('events', 'last_accessed_at', 'TEXT');
    this.addColumnIfMissing('events', 'turn_id', 'TEXT');

    // Create indexes for new columns if they don't exist
    try {
      sqliteExec(this.db, `
        CREATE INDEX IF NOT EXISTS idx_events_access_count ON events(access_count DESC);
      `);
    } catch (err: any) {
      // Index may already exist, ignore
    }

    try {
      sqliteExec(this.db, `
        CREATE INDEX IF NOT EXISTS idx_events_last_accessed ON events(last_accessed_at DESC);
      `);
    } catch (err: any) {
      // Index may already exist, ignore
    }

    try {
      sqliteExec(this.db, `
        CREATE INDEX IF NOT EXISTS idx_events_turn_id ON events(turn_id);
      `);
    } catch {
      // Index may already exist, ignore
    }

    // One-time backfill of the consolidated_memory_events junction from existing
    // source_events JSON (idempotent; skipped once the junction has any rows).
    const cmeCount = sqliteGet<{ c: number }>(this.db, `SELECT COUNT(*) as c FROM consolidated_memory_events`, []);
    if ((cmeCount?.c ?? 0) === 0) {
      sqliteExec(this.db, `
        INSERT OR IGNORE INTO consolidated_memory_events (memory_id, event_id)
        SELECT cm.memory_id, je.value
        FROM consolidated_memories cm, json_each(cm.source_events) je
        WHERE json_valid(cm.source_events);
      `);
    }

    // Stamp the schema version so future ordered migrations have an anchor to
    // gate on (PRAGMA user_version persists in the DB file). The migrations
    // above remain idempotent, so this is forward-looking rather than required.
    sqliteExec(this.db, `PRAGMA user_version = ${SQLITE_SCHEMA_VERSION}`);

    this.initialized = true;
  }

  /**
   * Append event to store (Append-only, Idempotent)
   */
  async append(input: MemoryEventInput): Promise<AppendResult> {
    await this.initialize();

    const canonicalKey = makeCanonicalKey(input.content);
    const dedupeKey = makeDedupeKey(input.content, input.sessionId);

    // Check for duplicate
    const existing = sqliteGet<{ event_id: string }>(
      this.db,
      `SELECT event_id FROM event_dedup WHERE dedupe_key = ?`,
      [dedupeKey]
    );

    if (existing) {
      try {
        await this.enqueueVectorOutboxEvent(existing.event_id);
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : String(error)
        };
      }
      return {
        success: true,
        eventId: existing.event_id,
        isDuplicate: true
      };
    }

    const id = randomUUID();
    const timestamp = toSQLiteTimestamp(input.timestamp);

    try {
      // Extract turnId from metadata if present
      const metadata = input.metadata || {};
      const turnId = (metadata.turnId as string) || null;

      // Use transaction for atomicity
      const insertEvent = this.db.prepare(`
        INSERT INTO events (id, event_type, session_id, timestamp, content, canonical_key, dedupe_key, metadata, turn_id)
        VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)
      `);

      const insertDedup = this.db.prepare(`
        INSERT INTO event_dedup (dedupe_key, event_id) VALUES (?, ?)
      `);

      const insertLevel = this.db.prepare(`
        INSERT INTO memory_levels (event_id, level) VALUES (?, 'L0')
      `);

      const transaction = this.db.transaction(() => {
        insertEvent.run(
          id,
          input.eventType,
          input.sessionId,
          timestamp,
          input.content,
          canonicalKey,
          dedupeKey,
          JSON.stringify(metadata),
          turnId
        );
        insertDedup.run(dedupeKey, id);
        insertLevel.run(id);
        this.enqueueVectorOutboxEventSync(id);
      });

      transaction();

      if (this.markdownMirror) {
        const event: MemoryEvent = {
          id,
          eventType: input.eventType,
          sessionId: input.sessionId,
          timestamp: input.timestamp,
          content: input.content,
          canonicalKey,
          dedupeKey,
          metadata
        };
        this.markdownMirror.append(event).catch((err) => {
          console.warn('[SQLiteEventStore] markdown mirror append failed:', err);
        });
      }

      return { success: true, eventId: id, isDuplicate: false };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : String(error)
      };
    }
  }

  /**
   * Get session IDs that have events but no session_summary event.
   * Used to backfill summaries for sessions that ended without Stop hook.
   */
  async getSessionsWithoutSummary(currentSessionId: string, limit = 5): Promise<string[]> {
    await this.initialize();
    const rows = sqliteAll<{ session_id: string }>(
      this.db,
      `SELECT DISTINCT e.session_id
       FROM events e
       WHERE e.session_id != ?
         AND e.event_type != 'session_summary'
         AND e.session_id NOT IN (
           SELECT DISTINCT session_id FROM events WHERE event_type = 'session_summary'
         )
       GROUP BY e.session_id
       HAVING COUNT(*) >= 3
       ORDER BY MAX(e.timestamp) DESC
       LIMIT ?`,
      [currentSessionId, limit]
    );
    return rows.map((r) => r.session_id);
  }

  /**
   * Get events by session ID
   */
  async getSessionEvents(sessionId: string, options?: QuarantineReadOptions): Promise<MemoryEvent[]> {
    await this.initialize();

    const rows = sqliteAll<Record<string, unknown>>(
      this.db,
      `SELECT * FROM events WHERE session_id = ? AND ${maybeQuarantinePredicate(options)} ORDER BY timestamp ASC`,
      [sessionId]
    );

    return rows.map(this.rowToEvent);
  }

  /**
   * Get recent events
   */
  async getRecentEvents(limit: number = 100, options?: QuarantineReadOptions): Promise<MemoryEvent[]> {
    await this.initialize();

    const rows = sqliteAll<Record<string, unknown>>(
      this.db,
      `SELECT * FROM events WHERE ${maybeQuarantinePredicate(options)} ORDER BY timestamp DESC LIMIT ?`,
      [limit]
    );

    return rows.map(this.rowToEvent);
  }

  /**
   * Aggregate event counts by type via SQL GROUP BY, instead of loading rows
   * into memory and counting in JS. Counts all events (the old recent-window
   * scan also under-counted stores larger than its cap).
   */
  async getEventTypeCounts(options?: QuarantineReadOptions): Promise<Array<{ eventType: string; count: number }>> {
    await this.initialize();
    const rows = sqliteAll<{ event_type: string; count: number }>(
      this.db,
      `SELECT event_type, COUNT(*) as count FROM events WHERE ${maybeQuarantinePredicate(options)} GROUP BY event_type`,
      []
    );
    return rows.map((row) => ({ eventType: row.event_type, count: row.count }));
  }

  /**
   * Fetch every event at/after an ISO timestamp (ascending), with no row cap.
   * For window-scoped analytics (KPI/usefulness) this fetches exactly the events
   * the window needs instead of an arbitrary "most recent N" slice that both
   * over-fetches sparse windows and truncates very active ones.
   */
  async getEventsAfter(sinceIso: string, options?: QuarantineReadOptions): Promise<MemoryEvent[]> {
    await this.initialize();
    const rows = sqliteAll<Record<string, unknown>>(
      this.db,
      `SELECT * FROM events WHERE timestamp >= ? AND ${maybeQuarantinePredicate(options)} ORDER BY timestamp ASC`,
      [sinceIso]
    );
    return rows.map((row) => this.rowToEvent(row));
  }

  /** Count distinct sessions via SQL instead of materializing a Set in JS. */
  async getDistinctSessionCount(options?: QuarantineReadOptions): Promise<number> {
    await this.initialize();
    const row = sqliteGet<{ count: number }>(
      this.db,
      `SELECT COUNT(DISTINCT session_id) as count FROM events WHERE ${maybeQuarantinePredicate(options)}`,
      []
    );
    return row?.count ?? 0;
  }

  /**
   * Per-day event counts (with type breakdown) since an ISO timestamp, computed
   * by SQL GROUP BY on the date prefix. Timestamps are stored as ISO-8601
   * strings, so substr(...,1,10) is the UTC day and `timestamp >= ?` compares
   * lexicographically.
   */
  async getDailyEventCounts(
    sinceIso: string,
    options?: QuarantineReadOptions
  ): Promise<Array<{ day: string; total: number; prompts: number; responses: number; tools: number }>> {
    await this.initialize();
    return sqliteAll<{ day: string; total: number; prompts: number; responses: number; tools: number }>(
      this.db,
      `SELECT substr(timestamp, 1, 10) as day,
              COUNT(*) as total,
              SUM(CASE WHEN event_type = 'user_prompt' THEN 1 ELSE 0 END) as prompts,
              SUM(CASE WHEN event_type = 'agent_response' THEN 1 ELSE 0 END) as responses,
              SUM(CASE WHEN event_type = 'tool_observation' THEN 1 ELSE 0 END) as tools
       FROM events
       WHERE timestamp >= ? AND ${maybeQuarantinePredicate(options)}
       GROUP BY day
       ORDER BY day ASC`,
      [sinceIso]
    );
  }

  /**
   * Get event by ID
   */
  async getEvent(id: string, options?: QuarantineReadOptions): Promise<MemoryEvent | null> {
    await this.initialize();

    const row = sqliteGet<Record<string, unknown>>(
      this.db,
      `SELECT * FROM events WHERE id = ? AND ${maybeQuarantinePredicate(options)}`,
      [id]
    );

    if (!row) return null;
    return this.rowToEvent(row);
  }

  /**
   * Batch-fetch events by id in a single query, applying the same quarantine
   * predicate as getEvent. Lets hot paths replace N sequential round-trips with
   * one. Missing ids are simply absent from the result (order not guaranteed).
   */
  async getEvents(ids: string[], options?: QuarantineReadOptions): Promise<MemoryEvent[]> {
    await this.initialize();
    if (ids.length === 0) return [];

    const placeholders = ids.map(() => '?').join(',');
    const rows = sqliteAll<Record<string, unknown>>(
      this.db,
      `SELECT * FROM events WHERE id IN (${placeholders}) AND ${maybeQuarantinePredicate(options)}`,
      ids
    );

    return rows.map((row) => this.rowToEvent(row));
  }

  /**
   * Resolve an event from its citation id via the reverse index, instead of
   * scanning recent events and hashing each one. The index is self-healing: a
   * miss triggers a one-time backfill of any events not yet indexed (covering
   * freshly appended events) before retrying.
   */
  async getEventByCitationId(citationId: string, options?: QuarantineReadOptions): Promise<MemoryEvent | null> {
    await this.initialize();
    if (!citationId) return null;

    let row = sqliteGet<{ event_id: string }>(
      this.db,
      `SELECT event_id FROM event_citations WHERE citation_id = ? LIMIT 1`,
      [citationId]
    );

    if (!row) {
      this.indexMissingCitations();
      row = sqliteGet<{ event_id: string }>(
        this.db,
        `SELECT event_id FROM event_citations WHERE citation_id = ? LIMIT 1`,
        [citationId]
      );
    }

    if (!row) return null;
    return this.getEvent(row.event_id, options);
  }

  /**
   * Backfill citation ids for any events not yet present in event_citations.
   * Gated on a cheap count comparison so the common (fully-indexed) path does no
   * work, and the scan only runs when there are genuinely new events to index.
   */
  private indexMissingCitations(): void {
    const eventCount = sqliteGet<{ c: number }>(this.db, `SELECT COUNT(*) as c FROM events`, [])?.c ?? 0;
    const indexedCount = sqliteGet<{ c: number }>(this.db, `SELECT COUNT(*) as c FROM event_citations`, [])?.c ?? 0;
    if (indexedCount >= eventCount) return;

    const unindexed = sqliteAll<{ id: string }>(
      this.db,
      `SELECT e.id FROM events e
       LEFT JOIN event_citations ec ON ec.event_id = e.id
       WHERE ec.event_id IS NULL`,
      []
    );
    if (unindexed.length === 0) return;

    const insert = this.db.prepare(`INSERT OR IGNORE INTO event_citations (event_id, citation_id) VALUES (?, ?)`);
    this.db.transaction(() => {
      for (const { id } of unindexed) {
        insert.run(id, generateCitationId(id));
      }
    })();
  }

  /**
   * Get events since a timestamp (for sync)
   */
  async getEventsSince(timestamp: string, limit: number = 1000, options?: QuarantineReadOptions): Promise<MemoryEvent[]> {
    await this.initialize();

    const rows = sqliteAll<Record<string, unknown>>(
      this.db,
      `SELECT * FROM events WHERE timestamp > ? AND ${maybeQuarantinePredicate(options)} ORDER BY timestamp ASC LIMIT ?`,
      [timestamp, limit]
    );

    return rows.map(this.rowToEvent);
  }

  /**
   * Get events since a SQLite rowid (for robust incremental replication).
   * Rowid is monotonic for append-only tables, independent of client timestamps.
   */
  async getEventsSinceRowid(
    lastRowid: number,
    limit: number = 1000,
    options?: QuarantineReadOptions
  ): Promise<Array<{ rowid: number; event: MemoryEvent }>> {
    await this.initialize();

    const rows = sqliteAll<Record<string, unknown>>(
      this.db,
      `SELECT rowid as _rowid, * FROM events WHERE rowid > ? AND ${maybeQuarantinePredicate(options)} ORDER BY rowid ASC LIMIT ?`,
      [lastRowid, limit]
    );

    return rows.map(row => ({
      rowid: row._rowid as number,
      event: this.rowToEvent(row)
    }));
  }

  /**
   * Import events with fixed IDs (used for cross-machine replication).
   * Idempotent: skips if event id or dedupeKey already exists.
   *
   * NOTE: This bypasses the append() id generation to preserve stable IDs.
   */
  async importEvents(events: MemoryEvent[]): Promise<{ inserted: number; skipped: number }> {
    if (events.length === 0) return { inserted: 0, skipped: 0 };
    if (this.readOnly) return { inserted: 0, skipped: events.length };

    await this.initialize();

    const getById = this.db.prepare(`SELECT id FROM events WHERE id = ?`);
    const getByDedupe = this.db.prepare(`SELECT event_id FROM event_dedup WHERE dedupe_key = ?`);

    const insertEvent = this.db.prepare(`
      INSERT INTO events (id, event_type, session_id, timestamp, content, canonical_key, dedupe_key, metadata, turn_id)
      VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)
    `);

    const insertDedup = this.db.prepare(`
      INSERT INTO event_dedup (dedupe_key, event_id) VALUES (?, ?)
    `);

    const insertLevel = this.db.prepare(`
      INSERT INTO memory_levels (event_id, level) VALUES (?, 'L0')
    `);

    let inserted = 0;
    let skipped = 0;
    const insertedEvents: MemoryEvent[] = [];

    const tx = this.db.transaction((batch: MemoryEvent[]) => {
      for (const ev of batch) {
        // Skip if already present by id
        const existingById = getById.get(ev.id) as { id: string } | undefined;
        if (existingById) {
          skipped++;
          continue;
        }

        const canonicalKey = ev.canonicalKey || makeCanonicalKey(ev.content);
        const dedupeKey = ev.dedupeKey || makeDedupeKey(ev.content, ev.sessionId);

        // Skip if already present by dedupe key
        const existingByDedupe = getByDedupe.get(dedupeKey) as { event_id: string } | undefined;
        if (existingByDedupe) {
          skipped++;
          continue;
        }

        const metadata = ev.metadata || {};
        const turnId = (metadata as any).turnId as string | undefined;

        insertEvent.run(
          ev.id,
          ev.eventType,
          ev.sessionId,
          toSQLiteTimestamp(ev.timestamp),
          ev.content,
          canonicalKey,
          dedupeKey,
          JSON.stringify(metadata),
          turnId ?? null
        );

        insertDedup.run(dedupeKey, ev.id);
        insertLevel.run(ev.id);
        this.enqueueVectorOutboxEventSync(ev.id);
        inserted++;
        insertedEvents.push(ev);
      }
    });

    tx(events);

    if (this.markdownMirror && insertedEvents.length > 0) {
      for (const ev of insertedEvents) {
        this.markdownMirror.append(ev).catch((err) => {
          console.warn('[SQLiteEventStore] markdown mirror append failed:', err);
        });
      }
    }

    return { inserted, skipped };
  }

  /**
   * Create or update session
   */
  async upsertSession(session: Partial<Session> & { id: string }): Promise<void> {
    await this.initialize();

    const existing = sqliteGet<{ id: string }>(
      this.db,
      `SELECT id FROM sessions WHERE id = ?`,
      [session.id]
    );

    if (!existing) {
      sqliteRun(
        this.db,
        `INSERT INTO sessions (id, started_at, project_path, tags)
         VALUES (?, ?, ?, ?)`,
        [
          session.id,
          toSQLiteTimestamp(session.startedAt || new Date()),
          session.projectPath || null,
          JSON.stringify(session.tags || [])
        ]
      );
    } else {
      const updates: string[] = [];
      const values: unknown[] = [];

      if (session.endedAt) {
        updates.push('ended_at = ?');
        values.push(toSQLiteTimestamp(session.endedAt));
      }
      if (session.summary) {
        updates.push('summary = ?');
        values.push(session.summary);
      }
      if (session.tags) {
        updates.push('tags = ?');
        values.push(JSON.stringify(session.tags));
      }

      if (updates.length > 0) {
        values.push(session.id);
        sqliteRun(
          this.db,
          `UPDATE sessions SET ${updates.join(', ')} WHERE id = ?`,
          values
        );
      }
    }
  }

  /**
   * Get session by ID
   */
  async getSession(id: string): Promise<Session | null> {
    await this.initialize();

    const row = sqliteGet<Record<string, unknown>>(
      this.db,
      `SELECT * FROM sessions WHERE id = ?`,
      [id]
    );

    if (!row) return null;

    return {
      id: row.id as string,
      startedAt: toDateFromSQLite(row.started_at),
      endedAt: row.ended_at ? toDateFromSQLite(row.ended_at) : undefined,
      projectPath: row.project_path as string | undefined,
      summary: row.summary as string | undefined,
      tags: row.tags ? JSON.parse(row.tags as string) : undefined
    };
  }

  /**
   * Get all sessions
   */
  async getAllSessions(): Promise<Session[]> {
    await this.initialize();

    const rows = sqliteAll<Record<string, unknown>>(
      this.db,
      `SELECT * FROM sessions ORDER BY started_at DESC`
    );

    return rows.map(row => ({
      id: row.id as string,
      startedAt: toDateFromSQLite(row.started_at),
      endedAt: row.ended_at ? toDateFromSQLite(row.ended_at) : undefined,
      projectPath: row.project_path as string | undefined,
      summary: row.summary as string | undefined,
      tags: row.tags ? JSON.parse(row.tags as string) : undefined
    }));
  }

  /**
   * Add to embedding outbox
   */
  async enqueueForEmbedding(eventId: string, content: string): Promise<string> {
    await this.initialize();

    const id = randomUUID();
    sqliteRun(
      this.db,
      `INSERT INTO embedding_outbox (id, event_id, content, status, retry_count)
       VALUES (?, ?, ?, 'pending', 0)`,
      [id, eventId, content]
    );

    return id;
  }

  /**
   * Get pending outbox items
   */
  async getPendingOutboxItems(limit: number = 32): Promise<OutboxItem[]> {
    await this.initialize();

    // Claim pending items atomically in a single UPDATE ... RETURNING. A
    // SELECT-then-UPDATE leaves a window where two concurrent workers (or a
    // worker plus processAll) both select the same rows before either marks them
    // 'processing', producing duplicate embedding work. The single statement
    // makes the claim race-free, matching VectorOutbox.claimJobs.
    const claimed = sqliteAll<Record<string, unknown>>(
      this.db,
      `UPDATE embedding_outbox
       SET status = 'processing', processed_at = datetime('now'), error_message = NULL
       WHERE id IN (
         SELECT id FROM embedding_outbox
         WHERE status = 'pending'
         ORDER BY created_at
         LIMIT ?
       )
       RETURNING *`,
      [limit]
    );

    if (claimed.length === 0) return [];

    // RETURNING does not guarantee row order; restore created_at ordering.
    claimed.sort((a, b) => String(a.created_at).localeCompare(String(b.created_at)));

    return claimed.map(row => ({
      id: row.id as string,
      eventId: row.event_id as string,
      content: row.content as string,
      status: 'processing' as const,
      retryCount: row.retry_count as number,
      createdAt: toDateFromSQLite(row.created_at),
      errorMessage: row.error_message as string | undefined
    }));
  }

  /**
   * Mark outbox items as done
   */
  async completeOutboxItems(ids: string[]): Promise<void> {
    if (ids.length === 0) return;

    const placeholders = ids.map(() => '?').join(',');
    sqliteRun(
      this.db,
      `DELETE FROM embedding_outbox WHERE id IN (${placeholders})`,
      ids
    );
  }

  /**
   * Clear embedding outbox (used for embedding model migration)
   */
  async clearEmbeddingOutbox(): Promise<void> {
    await this.initialize();
    sqliteRun(this.db, `DELETE FROM embedding_outbox`);
  }

  /**
   * Count total events
   */
  async countEvents(options?: QuarantineReadOptions): Promise<number> {
    await this.initialize();
    const row = sqliteGet<{ count: number }>(this.db, `SELECT COUNT(*) as count FROM events WHERE ${maybeQuarantinePredicate(options)}`);
    return row?.count || 0;
  }

  /**
   * Get events page in timestamp ascending order (stable migration/reindex scans)
   */
  async getEventsPage(limit: number = 1000, offset: number = 0, options?: QuarantineReadOptions): Promise<MemoryEvent[]> {
    await this.initialize();

    const rows = sqliteAll<Record<string, unknown>>(
      this.db,
      `SELECT * FROM events WHERE ${maybeQuarantinePredicate(options)} ORDER BY timestamp ASC LIMIT ? OFFSET ?`,
      [limit, offset]
    );

    return rows.map(this.rowToEvent);
  }

  /**
   * Mark outbox items as failed
   */
  async failOutboxItems(ids: string[], error: string): Promise<void> {
    if (ids.length === 0) return;

    const placeholders = ids.map(() => '?').join(',');
    sqliteRun(
      this.db,
      `UPDATE embedding_outbox
       SET status = CASE WHEN retry_count >= 3 THEN 'failed' ELSE 'pending' END,
           retry_count = retry_count + 1,
           error_message = ?
       WHERE id IN (${placeholders})`,
      [error, ...ids]
    );
  }

  /**
   * Recover abandoned outbox work after a worker/process crash.
   *
   * Rows in `processing` are claimed work. If the process exits before marking
   * them done/failed, they otherwise remain invisible to future processing.
   * Recovery is deliberately age-gated so an active worker is not disturbed.
   */
  async recoverStuckOutboxItems(options: OutboxRecoveryOptions = {}): Promise<OutboxRecoveryResult> {
    await this.initialize();

    const thresholdMs = Number.isFinite(options.stuckThresholdMs) && (options.stuckThresholdMs ?? 0) >= 0
      ? options.stuckThresholdMs!
      : DEFAULT_OUTBOX_STUCK_THRESHOLD_MS;
    const maxRetries = Number.isFinite(options.maxRetries) && (options.maxRetries ?? 0) > 0
      ? options.maxRetries!
      : DEFAULT_OUTBOX_MAX_RETRIES;
    const now = options.now ?? new Date();
    const threshold = new Date(now.getTime() - thresholdMs).toISOString();
    const result = emptyOutboxRecoveryResult();

    if (options.dryRun === true) {
      const embeddingRecovered = sqliteGet<{ count: number }>(
        this.db,
        `SELECT COUNT(*) AS count FROM embedding_outbox
         WHERE status = 'processing'
           AND datetime(COALESCE(processed_at, created_at)) < datetime(?)`,
        [threshold]
      );
      result.embedding.recoveredProcessing = Number(embeddingRecovered?.count ?? 0);

      const embeddingRetried = sqliteGet<{ count: number }>(
        this.db,
        `SELECT COUNT(*) AS count FROM embedding_outbox
         WHERE status = 'failed'
           AND retry_count < ?`,
        [maxRetries]
      );
      result.embedding.retriedFailed = Number(embeddingRetried?.count ?? 0);

      const vectorRecovered = sqliteGet<{ count: number }>(
        this.db,
        `SELECT COUNT(*) AS count FROM vector_outbox
         WHERE status = 'processing'
           AND datetime(updated_at) < datetime(?)`,
        [threshold]
      );
      result.vector.recoveredProcessing = Number(vectorRecovered?.count ?? 0);

      const vectorRetried = sqliteGet<{ count: number }>(
        this.db,
        `SELECT COUNT(*) AS count FROM vector_outbox
         WHERE status = 'failed'
           AND retry_count < ?`,
        [maxRetries]
      );
      result.vector.retriedFailed = Number(vectorRetried?.count ?? 0);

      return result;
    }

    const embeddingRecovered = sqliteRun(
      this.db,
      `UPDATE embedding_outbox
       SET status = 'pending', processed_at = NULL, error_message = NULL
       WHERE status = 'processing'
         AND datetime(COALESCE(processed_at, created_at)) < datetime(?)`,
      [threshold]
    );
    result.embedding.recoveredProcessing = Number(embeddingRecovered.changes ?? 0);

    const embeddingRetried = sqliteRun(
      this.db,
      `UPDATE embedding_outbox
       SET status = 'pending', error_message = NULL
       WHERE status = 'failed'
         AND retry_count < ?`,
      [maxRetries]
    );
    result.embedding.retriedFailed = Number(embeddingRetried.changes ?? 0);

    const vectorRecovered = sqliteRun(
      this.db,
      `UPDATE vector_outbox
       SET status = 'pending', updated_at = ?, error = NULL
       WHERE status = 'processing'
         AND datetime(updated_at) < datetime(?)`,
      [now.toISOString(), threshold]
    );
    result.vector.recoveredProcessing = Number(vectorRecovered.changes ?? 0);

    const vectorRetried = sqliteRun(
      this.db,
      `UPDATE vector_outbox
       SET status = 'pending', updated_at = ?, error = NULL
       WHERE status = 'failed'
         AND retry_count < ?`,
      [now.toISOString(), maxRetries]
    );
    result.vector.retriedFailed = Number(vectorRetried.changes ?? 0);

    return result;
  }


  /**
   * Repair legacy imported events that predate canonical project scope metadata.
   *
   * Same-project legacy rows are tagged with scope.project.hash. Rows that look
   * imported but cannot be proven to belong to this project are quarantined so
   * dashboard default reads/search do not surface cross-project contamination.
   */
  async repairLegacyProjectScope(options: ProjectScopeRepairOptions = {}): Promise<ProjectScopeRepairResult> {
    await this.initialize();

    const projectHash = options.projectHash || (options.projectPath ? hashProjectPath(options.projectPath) : undefined);
    if (!projectHash) {
      throw new Error('repairLegacyProjectScope requires projectPath or projectHash');
    }
    if (options.projectPath && options.projectHash && hashProjectPath(options.projectPath) !== options.projectHash) {
      throw new Error('repairLegacyProjectScope projectPath and projectHash refer to different project stores');
    }

    const dryRun = options.dryRun === true;
    const nowIso = (options.now || new Date()).toISOString();
    const result = buildRepairResult(projectHash, dryRun);

    const rows = sqliteAll<{
      id: string;
      content: string;
      metadata: string | null;
      session_project_path: string | null;
    }>(
      this.db,
      `SELECT e.id, e.content, e.metadata, s.project_path as session_project_path
       FROM events e
       LEFT JOIN sessions s ON s.id = e.session_id
       ORDER BY e.timestamp ASC`,
      []
    );

    const sample = (entry: ProjectScopeRepairSample) => {
      if (result.samples.length < 20) result.samples.push(entry);
    };

    for (const row of rows) {
      result.scanned++;

      let metadata: Record<string, unknown> = {};
      let metadataParseInvalid = false;
      if (row.metadata) {
        const parsed = safeParseMetadataValue(row.metadata);
        if (parsed) {
          metadata = parsed;
        } else {
          metadataParseInvalid = true;
        }
      }

      if (isActiveQuarantinedMetadata(metadata)) {
        result.skipped++;
        continue;
      }

      const currentHash = metadataProjectHash(metadata);
      const explicitPath = metadataProjectPath(metadata);
      const sessionProjectPath = typeof row.session_project_path === 'string' && row.session_project_path.length > 0
        ? row.session_project_path
        : undefined;
      const candidatePaths = metadataProjectPaths(metadata);
      if (sessionProjectPath && !candidatePaths.includes(sessionProjectPath)) {
        candidatePaths.push(sessionProjectPath);
      }
      const importedOrLegacy = metadataParseInvalid || isImportedOrLegacyScopedMetadata(metadata) || Boolean(sessionProjectPath);
      const pathHashes = candidatePaths.map((candidate) => {
        try {
          return { path: candidate, hash: hashProjectPath(candidate) };
        } catch {
          return { path: candidate, hash: undefined };
        }
      });
      const matchingPath = pathHashes.find((candidate) => candidate.hash === projectHash);
      const foreignPath = pathHashes.find((candidate) => candidate.hash && candidate.hash !== projectHash);

      let action: 'repaired' | 'quarantined' | 'skipped' = 'skipped';
      let reason: ProjectScopeRepairSample['reason'] | undefined;
      let observedProjectHash: string | undefined;

      if (foreignPath) {
        action = 'quarantined';
        reason = 'project-path-mismatch';
        observedProjectHash = foreignPath.hash;
      } else if (currentHash === projectHash && importedOrLegacy && hasConflictingContentProjectHint(row.content, options.projectPath)) {
        action = 'quarantined';
        reason = 'content-project-mismatch';
      } else if (currentHash === projectHash) {
        result.alreadyScoped++;
        continue;
      } else if (currentHash && currentHash !== projectHash) {
        action = 'quarantined';
        reason = 'scope-hash-mismatch';
        observedProjectHash = currentHash;
      } else if (matchingPath) {
        action = 'repaired';
        reason = matchingPath.path === sessionProjectPath && matchingPath.path !== explicitPath
          ? 'session-project-path'
          : 'same-project-path';
      } else if (candidatePaths.length > 0) {
        action = 'quarantined';
        reason = 'project-path-mismatch';
      } else if (importedOrLegacy) {
        action = 'quarantined';
        reason = 'missing-project-scope';
      }

      if (action === 'skipped' || !reason) {
        result.skipped++;
        continue;
      }

      if (action === 'repaired') {
        const scope = isRecord(metadata.scope) ? { ...metadata.scope } : {};
        const project = isRecord(scope.project) ? { ...scope.project } : {};
        project.hash = projectHash;
        scope.project = project;
        metadata.scope = scope;
        metadata.repair = {
          ...(isRecord(metadata.repair) ? metadata.repair : {}),
          legacyProjectScope: {
            action,
            reason,
            repairedAt: nowIso
          }
        };
        addMetadataTag(metadata, `proj:${projectHash}`);
        result.repaired++;
      } else {
        metadata.quarantine = {
          ...(isRecord(metadata.quarantine) ? metadata.quarantine : {}),
          status: 'active',
          category: 'project-scope',
          reason,
          detectedAt: nowIso,
          expectedProjectHash: projectHash,
          ...(observedProjectHash ? { observedProjectHash } : {})
        };
        metadata.repair = {
          ...(isRecord(metadata.repair) ? metadata.repair : {}),
          legacyProjectScope: {
            action,
            reason,
            repairedAt: nowIso
          }
        };
        addMetadataTag(metadata, 'quarantine:project-scope');
        result.quarantined++;
      }

      sample({ eventId: row.id, action, reason });
      if (!dryRun) {
        sqliteRun(this.db, `UPDATE events SET metadata = ? WHERE id = ?`, [JSON.stringify(metadata), row.id]);
      }
    }

    return result;
  }

  /**
   * Get embedding/vector outbox health statistics
   */
  async getOutboxStats(options: OutboxStatsOptions = {}): Promise<OutboxStats> {
    await this.initialize();

    const thresholdMs = Number.isFinite(options.stuckThresholdMs) && (options.stuckThresholdMs ?? 0) >= 0
      ? options.stuckThresholdMs!
      : DEFAULT_OUTBOX_STUCK_THRESHOLD_MS;
    const maxRetries = Number.isFinite(options.maxRetries) && (options.maxRetries ?? 0) > 0
      ? options.maxRetries!
      : DEFAULT_OUTBOX_MAX_RETRIES;
    const now = options.now ?? new Date();
    const threshold = new Date(now.getTime() - thresholdMs).toISOString();

    const embeddingRows = sqliteAll<{ status: string; count: number }>(
      this.db,
      `SELECT status, COUNT(*) as count FROM embedding_outbox GROUP BY status`
    );
    const vectorRows = sqliteAll<{ status: string; count: number }>(
      this.db,
      `SELECT status, COUNT(*) as count FROM vector_outbox GROUP BY status`
    );

    const processingAgeMs = (value: unknown): number | null => {
      if (value === null || value === undefined) return null;
      const date = toDateFromSQLite(value);
      const time = date.getTime();
      if (!Number.isFinite(time)) return null;
      return Math.max(0, now.getTime() - time);
    };

    const fromRows = (
      rows: Array<{ status: string; count: number }>,
      stuckProcessing: number,
      oldestProcessingAgeMs: number | null,
      retryableFailed: number,
      quarantinedFailed: number
    ) => {
      const out = { pending: 0, processing: 0, failed: 0, retryableFailed, quarantinedFailed, total: 0, stuckProcessing, oldestProcessingAgeMs };
      for (const row of rows) {
        const key = row.status as 'pending' | 'processing' | 'failed' | 'done';
        if (key === 'pending' || key === 'processing' || key === 'failed') {
          out[key] += Number(row.count ?? 0);
        }
        out.total += Number(row.count ?? 0);
      }
      return out;
    };

    const embeddingStuck = sqliteGet<{ count: number }>(
      this.db,
      `SELECT COUNT(*) as count
       FROM embedding_outbox
       WHERE status = 'processing'
         AND datetime(COALESCE(processed_at, created_at)) < datetime(?)`,
      [threshold]
    );
    const embeddingOldest = sqliteGet<{ oldest: string | null }>(
      this.db,
      `SELECT MIN(datetime(COALESCE(processed_at, created_at))) as oldest
       FROM embedding_outbox
       WHERE status = 'processing'`
    );
    const embeddingRetryableFailed = sqliteGet<{ count: number }>(
      this.db,
      `SELECT COUNT(*) as count
       FROM embedding_outbox
       WHERE status = 'failed'
         AND retry_count < ?`,
      [maxRetries]
    );
    const embeddingQuarantinedFailed = sqliteGet<{ count: number }>(
      this.db,
      `SELECT COUNT(*) as count
       FROM embedding_outbox
       WHERE status = 'failed'
         AND retry_count >= ?`,
      [maxRetries]
    );

    const vectorStuck = sqliteGet<{ count: number }>(
      this.db,
      `SELECT COUNT(*) as count
       FROM vector_outbox
       WHERE status = 'processing'
         AND datetime(updated_at) < datetime(?)`,
      [threshold]
    );
    const vectorOldest = sqliteGet<{ oldest: string | null }>(
      this.db,
      `SELECT MIN(datetime(updated_at)) as oldest
       FROM vector_outbox
       WHERE status = 'processing'`
    );
    const vectorRetryableFailed = sqliteGet<{ count: number }>(
      this.db,
      `SELECT COUNT(*) as count
       FROM vector_outbox
       WHERE status = 'failed'
         AND retry_count < ?`,
      [maxRetries]
    );
    const vectorQuarantinedFailed = sqliteGet<{ count: number }>(
      this.db,
      `SELECT COUNT(*) as count
       FROM vector_outbox
       WHERE status = 'failed'
         AND retry_count >= ?`,
      [maxRetries]
    );

    return {
      embedding: fromRows(
        embeddingRows,
        Number(embeddingStuck?.count ?? 0),
        processingAgeMs(embeddingOldest?.oldest),
        Number(embeddingRetryableFailed?.count ?? 0),
        Number(embeddingQuarantinedFailed?.count ?? 0)
      ),
      vector: fromRows(
        vectorRows,
        Number(vectorStuck?.count ?? 0),
        processingAgeMs(vectorOldest?.oldest),
        Number(vectorRetryableFailed?.count ?? 0),
        Number(vectorQuarantinedFailed?.count ?? 0)
      )
    };
  }

  /**
   * Update memory level
   */
  async updateMemoryLevel(eventId: string, level: string): Promise<void> {
    await this.initialize();

    sqliteRun(
      this.db,
      `UPDATE memory_levels SET level = ?, promoted_at = datetime('now') WHERE event_id = ?`,
      [level, eventId]
    );
  }

  /**
   * Get memory level statistics
   */
  async getLevelStats(): Promise<Array<{ level: string; count: number }>> {
    await this.initialize();

    const rows = sqliteAll<{ level: string; count: number }>(
      this.db,
      `SELECT ml.level, COUNT(*) as count
       FROM memory_levels ml
       INNER JOIN events e ON e.id = ml.event_id
       WHERE ${notActiveQuarantinedSql('e.metadata')}
       GROUP BY ml.level`
    );

    return rows;
  }

  /**
   * Get events by memory level
   */
  async getEventsByLevel(level: string, options?: { limit?: number; offset?: number }): Promise<MemoryEvent[]> {
    await this.initialize();

    const limit = options?.limit || 50;
    const offset = options?.offset || 0;

    const rows = sqliteAll<Record<string, unknown>>(
      this.db,
      `SELECT e.* FROM events e
       INNER JOIN memory_levels ml ON e.id = ml.event_id
       WHERE ml.level = ?
         AND ${notActiveQuarantinedSql('e.metadata')}
       ORDER BY e.timestamp DESC
       LIMIT ? OFFSET ?`,
      [level, limit, offset]
    );

    return rows.map(row => this.rowToEvent(row));
  }

  /**
   * Get memory level for a specific event
   */
  async getEventLevel(eventId: string): Promise<string | null> {
    await this.initialize();

    const row = sqliteGet<{ level: string }>(
      this.db,
      `SELECT level FROM memory_levels WHERE event_id = ?`,
      [eventId]
    );

    return row ? row.level : null;
  }

  /**
   * Get sync position for a target
   */
  async getSyncPosition(targetName: string): Promise<{ lastEventId: string | null; lastTimestamp: string | null }> {
    await this.initialize();

    const row = sqliteGet<{ last_event_id: string | null; last_timestamp: string | null }>(
      this.db,
      `SELECT last_event_id, last_timestamp FROM sync_positions WHERE target_name = ?`,
      [targetName]
    );

    return {
      lastEventId: row?.last_event_id ?? null,
      lastTimestamp: row?.last_timestamp ?? null
    };
  }

  /**
   * Update sync position for a target
   */
  async updateSyncPosition(targetName: string, lastEventId: string, lastTimestamp: string): Promise<void> {
    await this.initialize();

    sqliteRun(
      this.db,
      `INSERT OR REPLACE INTO sync_positions (target_name, last_event_id, last_timestamp, updated_at)
       VALUES (?, ?, ?, datetime('now'))`,
      [targetName, lastEventId, lastTimestamp]
    );
  }

  /**
   * Get config value for endless mode
   */
  async getEndlessConfig(key: string): Promise<unknown | null> {
    await this.initialize();

    const row = sqliteGet<{ value: string }>(
      this.db,
      `SELECT value FROM endless_config WHERE key = ?`,
      [key]
    );

    if (!row) return null;
    return JSON.parse(row.value);
  }

  /**
   * Set config value for endless mode
   */
  async setEndlessConfig(key: string, value: unknown): Promise<void> {
    await this.initialize();

    sqliteRun(
      this.db,
      `INSERT OR REPLACE INTO endless_config (key, value, updated_at)
       VALUES (?, ?, datetime('now'))`,
      [key, JSON.stringify(value)]
    );
  }

  /**
   * Increment access count for events
   */
  async incrementAccessCount(eventIds: string[]): Promise<void> {
    if (eventIds.length === 0 || this.readOnly) return;

    await this.initialize();

    const placeholders = eventIds.map(() => '?').join(',');
    const currentTime = toSQLiteTimestamp(new Date());

    sqliteRun(
      this.db,
      `UPDATE events
       SET access_count = access_count + 1,
           last_accessed_at = ?
       WHERE id IN (${placeholders})`,
      [currentTime, ...eventIds]
    );
  }

  /**
   * Get most accessed memories (falls back to recent events if none accessed)
   */
  async getMostAccessed(limit: number = 10, options?: QuarantineReadOptions): Promise<MemoryEvent[]> {
    await this.initialize();

    // First try events with access_count > 0
    let rows = sqliteAll<Record<string, unknown>>(
      this.db,
      `SELECT * FROM events
       WHERE access_count > 0
         AND ${maybeQuarantinePredicate(options)}
       ORDER BY access_count DESC, last_accessed_at DESC
       LIMIT ?`,
      [limit]
    );

    // Fallback: if no accessed events, show recent events
    if (rows.length === 0) {
      rows = sqliteAll<Record<string, unknown>>(
        this.db,
        `SELECT * FROM events
         WHERE ${maybeQuarantinePredicate(options)}
         ORDER BY timestamp DESC
         LIMIT ?`,
        [limit]
      );
    }

    return rows.map(row => this.rowToEvent(row));
  }

  /**
   * Record a memory retrieval for helpfulness tracking
   */
  async recordRetrieval(
    eventId: string,
    sessionId: string,
    score: number,
    query: string,
    options?: { traceId?: string; source?: string; injectedContent?: string }
  ): Promise<void> {
    if (this.readOnly) return;
    await this.initialize();

    const id = randomUUID();
    // created_at needs millisecond precision: datetime('now') is second-only,
    // so the triggering prompt (stored moments earlier with an ISO ms
    // timestamp in the same second) would count as "after" the retrieval.
    sqliteRun(
      this.db,
      `INSERT INTO memory_helpfulness (id, event_id, session_id, retrieval_score, query_preview, trace_id, source, injected_content, created_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        id, eventId, sessionId, score, query.slice(0, 200),
        options?.traceId || null,
        options?.source || 'user_prompt',
        options?.injectedContent ? options.injectedContent.slice(0, 2000) : null,
        new Date().toISOString()
      ]
    );
  }

  /**
   * Get session IDs that have unevaluated retrievals (measured_at IS NULL).
   * Excludes the current session. Used to backfill sessions that ended without Stop hook.
   */
  async getUnevaluatedSessions(currentSessionId: string, limit = 5): Promise<string[]> {
    await this.initialize();
    const rows = sqliteAll<{ session_id: string }>(
      this.db,
      `SELECT DISTINCT session_id FROM memory_helpfulness
       WHERE measured_at IS NULL AND session_id != ?
       ORDER BY created_at DESC LIMIT ?`,
      [currentSessionId, limit]
    );
    return rows.map((r) => r.session_id);
  }

  /**
   * Evaluate helpfulness for all retrievals in a session
   * Called at session end - uses behavioral signals to compute score
   */
  async evaluateSessionHelpfulness(sessionId: string): Promise<void> {
    if (this.readOnly) return;
    await this.initialize();

    // Get all retrieval records for this session
    const retrievals = sqliteAll<Record<string, unknown>>(
      this.db,
      `SELECT * FROM memory_helpfulness WHERE session_id = ? AND measured_at IS NULL`,
      [sessionId]
    );

    if (retrievals.length === 0) return;

    // Get session events to analyze behavior after retrieval
    const sessionEvents = sqliteAll<Record<string, unknown>>(
      this.db,
      `SELECT * FROM events WHERE session_id = ? ORDER BY timestamp ASC`,
      [sessionId]
    );

    const promptEvents = sessionEvents.filter((e: any) => e.event_type === 'user_prompt');
    const toolEvents = sessionEvents.filter((e: any) => e.event_type === 'tool_observation');
    const responseEvents = sessionEvents.filter((e: any) => e.event_type === 'agent_response');

    // Look up the injected memories' content once so each retrieval can be
    // checked for content grounding against the responses that followed it.
    const retrievedEventIds = Array.from(new Set(retrievals.map((r) => r.event_id as string).filter(Boolean)));
    const memoryContentById = new Map<string, string>();
    for (let i = 0; i < retrievedEventIds.length; i += 100) {
      const chunk = retrievedEventIds.slice(i, i + 100);
      const rows = sqliteAll<{ id: string; content: string }>(
        this.db,
        `SELECT id, content FROM events WHERE id IN (${chunk.map(() => '?').join(',')})`,
        chunk
      );
      for (const row of rows) memoryContentById.set(row.id, row.content);
    }

    // Count successful vs failed tools
    let toolSuccessCount = 0;
    const toolTotalCount = toolEvents.length;
    for (const t of toolEvents) {
      try {
        const content = JSON.parse(t.content as string);
        if (content.success !== false) toolSuccessCount++;
      } catch {
        toolSuccessCount++; // Assume success if can't parse
      }
    }
    const toolSuccessRatio = toolTotalCount > 0 ? toolSuccessCount / toolTotalCount : 0.5;

    // Events store ISO timestamps while memory_helpfulness.created_at uses
    // SQLite datetime('now') ("YYYY-MM-DD HH:MM:SS", UTC). Comparing the raw
    // strings marks any same-day event as "after", so compare epoch millis.
    const toEpochMs = (value: unknown): number => {
      const raw = String(value || '');
      const iso = raw.includes('T') ? raw : `${raw.replace(' ', 'T')}Z`;
      const ms = new Date(iso).getTime();
      return Number.isFinite(ms) ? ms : 0;
    };

    for (const retrieval of retrievals) {
      const retrievalTimeMs = toEpochMs(retrieval.created_at);

      // 1. Session continued after retrieval?
      const eventsAfter = sessionEvents.filter((e: any) => toEpochMs(e.timestamp) > retrievalTimeMs);
      const sessionContinued = eventsAfter.length > 0 ? 1 : 0;

      // 2. How many prompts came after?
      const promptsAfter = promptEvents.filter((e: any) => toEpochMs(e.timestamp) > retrievalTimeMs);
      const promptCountAfter = promptsAfter.length;

      // 3. Was a similar query asked again? (simple word overlap check)
      const queryWords = new Set((retrieval.query_preview as string || '').toLowerCase().split(/\s+/).filter(w => w.length > 2));
      let wasReasked = 0;
      for (const p of promptsAfter) {
        const pWords = new Set((p.content as string).toLowerCase().split(/\s+/).filter((w: string) => w.length > 2));
        let overlap = 0;
        for (const w of queryWords) {
          if (pWords.has(w)) overlap++;
        }
        if (queryWords.size > 0 && overlap / queryWords.size > 0.5) {
          wasReasked = 1;
          break;
        }
      }

      // 4. Content grounding: did the assistant responses after this
      //    retrieval actually reuse the memory's content? This is the most
      //    direct usefulness signal we have — the behavioral signals below
      //    only approximate it.
      const responsesAfter = responseEvents
        .filter((e: any) => toEpochMs(e.timestamp) > retrievalTimeMs)
        .map((e: any) => ({ id: e.id as string, content: e.content as string }));
      // Grounding must be checked against what was actually injected into the
      // prompt (hooks truncate memories), not the full stored event — otherwise
      // facts the model never saw would count as "used". Full content is the
      // fallback for legacy rows recorded before the snapshot column existed.
      const memoryContent = (retrieval.injected_content as string)
        || memoryContentById.get(retrieval.event_id as string)
        || '';
      let contentOverlapScore: number | null = null;
      let evidenceMatches: EvidenceMatch[] = [];
      if (responsesAfter.length > 0 && memoryContent) {
        const evidence = computeMemoryUsageEvidence(memoryContent, responsesAfter);
        contentOverlapScore = evidence.contentOverlapScore;
        evidenceMatches = evidence.matches;
      }

      // Calculate helpfulness score
      // Weights tuned for shopping-assistant-like corpora where sessions
      // continue on the same topic (was_reasked was over-penalising normal conversation flow)
      const retrievalScore = retrieval.retrieval_score as number || 0;
      // More prompts after retrieval = memory was actually useful to the conversation
      const promptNorm = Math.min(promptCountAfter / 2, 1.0);
      // When content grounding is measurable it dominates the score; the
      // legacy behavioral-only formula remains the fallback (e.g. session
      // ended right after retrieval, so no responses followed).
      const helpfulnessScore = contentOverlapScore !== null
        ? (
          0.45 * contentOverlapScore +
          0.20 * Math.min(retrievalScore, 1.0) +
          0.15 * promptNorm +
          0.10 * toolSuccessRatio +
          0.10 * (sessionContinued ? 1.0 : 0.0)
        )
        : (
          0.40 * Math.min(retrievalScore, 1.0) +
          0.30 * promptNorm +
          0.20 * toolSuccessRatio +
          0.10 * (sessionContinued ? 1.0 : 0.0)
        );

      sqliteRun(
        this.db,
        `UPDATE memory_helpfulness
         SET session_continued = ?, prompt_count_after = ?,
             tool_success_count = ?, tool_total_count = ?,
             was_reasked = ?, helpfulness_score = ?,
             content_overlap_score = ?, evidence_json = ?,
             measured_at = datetime('now')
         WHERE id = ?`,
        [sessionContinued, promptCountAfter, toolSuccessCount, toolTotalCount,
         wasReasked, helpfulnessScore,
         contentOverlapScore,
         evidenceMatches.length > 0 ? JSON.stringify(evidenceMatches) : null,
         retrieval.id]
      );
    }
  }

  /**
   * Get most helpful memories ranked by helpfulness score
   */
  async getHelpfulMemories(limit: number = 10): Promise<Array<{
    eventId: string;
    summary: string;
    helpfulnessScore: number;
    accessCount: number;
    evaluationCount: number;
  }>> {
    await this.initialize();

    const rows = sqliteAll<Record<string, unknown>>(
      this.db,
      `SELECT
         mh.event_id,
         AVG(mh.helpfulness_score) as avg_score,
         COUNT(*) as eval_count,
         e.content,
         e.access_count
       FROM memory_helpfulness mh
       JOIN events e ON e.id = mh.event_id
       WHERE mh.measured_at IS NOT NULL
         AND ${notActiveQuarantinedSql('e.metadata')}
       GROUP BY mh.event_id
       ORDER BY avg_score DESC
       LIMIT ?`,
      [limit]
    );

    return rows.map(r => ({
      eventId: r.event_id as string,
      summary: (r.content as string).substring(0, 200) + ((r.content as string).length > 200 ? '...' : ''),
      helpfulnessScore: Math.round((r.avg_score as number) * 100) / 100,
      accessCount: (r.access_count as number) || 0,
      evaluationCount: r.eval_count as number
    }));
  }

  /**
   * Get helpfulness statistics for dashboard
   */
  async getHelpfulnessStats(since?: Date): Promise<{
    avgScore: number;
    totalEvaluated: number;
    totalRetrievals: number;
    helpful: number;
    neutral: number;
    unhelpful: number;
    contentEvaluated: number;
    avgContentOverlap: number;
    groundedCount: number;
  }> {
    await this.initialize();

    const sinceIso = since?.toISOString();
    const evaluatedWhere = sinceIso
      ? `WHERE measured_at IS NOT NULL AND datetime(created_at) >= datetime(?)`
      : `WHERE measured_at IS NOT NULL`;
    const totalWhere = sinceIso
      ? `WHERE datetime(created_at) >= datetime(?)`
      : ``;

    // Read-only dashboards can open legacy DBs where the grounding-column
    // migration never ran; degrade to zeros instead of failing the query.
    const hasGroundingColumns = this.hasTableColumn('memory_helpfulness', 'content_overlap_score');
    const groundingSelect = hasGroundingColumns
      ? `COUNT(content_overlap_score) as content_evaluated,
         AVG(content_overlap_score) as avg_content_overlap,
         SUM(CASE WHEN content_overlap_score >= 0.3 THEN 1 ELSE 0 END) as grounded_count`
      : `0 as content_evaluated, 0 as avg_content_overlap, 0 as grounded_count`;

    const stats = sqliteGet<Record<string, unknown>>(
      this.db,
      `SELECT
         AVG(helpfulness_score) as avg_score,
         COUNT(*) as total_evaluated,
         SUM(CASE WHEN helpfulness_score >= 0.7 THEN 1 ELSE 0 END) as helpful,
         SUM(CASE WHEN helpfulness_score >= 0.4 AND helpfulness_score < 0.7 THEN 1 ELSE 0 END) as neutral,
         SUM(CASE WHEN helpfulness_score < 0.4 THEN 1 ELSE 0 END) as unhelpful,
         ${groundingSelect}
       FROM memory_helpfulness
       ${evaluatedWhere}`,
      sinceIso ? [sinceIso] : []
    );

    const totalRow = sqliteGet<Record<string, unknown>>(
      this.db,
      `SELECT COUNT(*) as total FROM memory_helpfulness ${totalWhere}`,
      sinceIso ? [sinceIso] : []
    );

    return {
      avgScore: Math.round(((stats?.avg_score as number) || 0) * 100) / 100,
      totalEvaluated: (stats?.total_evaluated as number) || 0,
      totalRetrievals: (totalRow?.total as number) || 0,
      helpful: (stats?.helpful as number) || 0,
      neutral: (stats?.neutral as number) || 0,
      unhelpful: (stats?.unhelpful as number) || 0,
      contentEvaluated: (stats?.content_evaluated as number) || 0,
      avgContentOverlap: Math.round(((stats?.avg_content_overlap as number) || 0) * 100) / 100,
      groundedCount: (stats?.grounded_count as number) || 0
    };
  }

  /**
   * Per-question usefulness history: each retrieval query (or session-start
   * injection batch) with the memories it injected, their measured
   * helpfulness, content grounding, and evidence snippets. Powers the
   * dashboard's evidence-history drill-down.
   */
  async getUsefulnessHistory(options: {
    limit?: number;
    offset?: number;
    sessionId?: string;
    withSelectionsOnly?: boolean;
  } = {}): Promise<Array<{
    traceId: string | null;
    kind: 'query' | 'session_start';
    sessionId: string | null;
    question: string;
    queryText: string | null;
    strategy: string | null;
    confidence: string | null;
    candidateCount: number;
    selectedCount: number;
    createdAt: Date;
    memories: Array<{
      eventId: string;
      eventType: string | null;
      summary: string;
      retrievalScore: number;
      helpfulnessScore: number | null;
      contentOverlapScore: number | null;
      evidence: EvidenceMatch[];
      measuredAt: string | null;
      source: string;
    }>;
  }>> {
    await this.initialize();

    const limit = Math.min(Math.max(options.limit ?? 20, 1), 100);
    const offset = Math.max(options.offset ?? 0, 0);

    // Read-only dashboards can open legacy DBs where the trace-link migration
    // (trace_id/source columns) never ran; fall back to trace-only history.
    const hasTraceLink = this.hasTableColumn('memory_helpfulness', 'trace_id');

    const sessionFilterTrace = options.sessionId ? `AND session_id = ?` : '';
    const selectionFilter = options.withSelectionsOnly ? `AND selected_count > 0` : '';
    const params: unknown[] = [];
    if (options.sessionId) params.push(options.sessionId);
    if (hasTraceLink && options.sessionId) params.push(options.sessionId);
    params.push(limit, offset);

    // Unified, paginated timeline of "questions": retrieval traces plus
    // session-start injection batches (which have no retrieval_traces row).
    const sessionStartBranch = hasTraceLink
      ? `UNION ALL
         SELECT 'session_start' AS kind,
                COALESCE(trace_id, 'ss-' || session_id) AS id,
                session_id,
                MIN(created_at) AS created_at
         FROM memory_helpfulness
         WHERE source = 'session_start' ${sessionFilterTrace}
         GROUP BY COALESCE(trace_id, 'ss-' || session_id), session_id`
      : '';
    const heads = sqliteAll<Record<string, unknown>>(
      this.db,
      `SELECT * FROM (
         SELECT 'query' AS kind, trace_id AS id, session_id, created_at
         FROM retrieval_traces
         WHERE 1=1 ${sessionFilterTrace} ${selectionFilter}
         ${sessionStartBranch}
       )
       ORDER BY created_at DESC
       LIMIT ? OFFSET ?`,
      params
    );

    const entries: Array<{
      traceId: string | null;
      kind: 'query' | 'session_start';
      sessionId: string | null;
      question: string;
      queryText: string | null;
      strategy: string | null;
      confidence: string | null;
      candidateCount: number;
      selectedCount: number;
      createdAt: Date;
      memories: Array<{
        eventId: string;
        eventType: string | null;
        summary: string;
        retrievalScore: number;
        helpfulnessScore: number | null;
        contentOverlapScore: number | null;
        evidence: EvidenceMatch[];
        measuredAt: string | null;
        source: string;
      }>;
    }> = [];

    for (const head of heads) {
      const kind = head.kind as 'query' | 'session_start';
      const headId = head.id as string;

      let trace: Record<string, unknown> | undefined;
      if (kind === 'query') {
        trace = sqliteGet<Record<string, unknown>>(
          this.db,
          `SELECT * FROM retrieval_traces WHERE trace_id = ?`,
          [headId]
        );
        if (!trace) continue;
      }

      // Helpfulness rows for this head: primary link via trace_id; legacy
      // fallback matches by session + selected event id within ±3 minutes.
      let helpRows: Record<string, unknown>[] = hasTraceLink
        ? sqliteAll<Record<string, unknown>>(
          this.db,
          `SELECT * FROM memory_helpfulness WHERE trace_id = ? ORDER BY created_at ASC`,
          [headId]
        )
        : [];
      if (kind === 'query' && helpRows.length === 0 && trace) {
        const selectedIds: string[] = (() => {
          try { return JSON.parse((trace.selected_event_ids as string) || '[]'); } catch { return []; }
        })();
        if (selectedIds.length > 0 && trace.session_id) {
          const legacyOnlyFilter = hasTraceLink ? `AND trace_id IS NULL` : '';
          helpRows = sqliteAll<Record<string, unknown>>(
            this.db,
            `SELECT * FROM memory_helpfulness
             WHERE session_id = ? ${legacyOnlyFilter}
               AND event_id IN (${selectedIds.map(() => '?').join(',')})
               AND ABS(strftime('%s', created_at) - strftime('%s', ?)) <= 180
             ORDER BY created_at ASC`,
            [trace.session_id, ...selectedIds, trace.created_at]
          );
        }
      }
      if (kind === 'session_start' && helpRows.length === 0 && headId.startsWith('ss-')) {
        helpRows = sqliteAll<Record<string, unknown>>(
          this.db,
          `SELECT * FROM memory_helpfulness
           WHERE source = 'session_start' AND trace_id IS NULL AND session_id = ?
           ORDER BY created_at ASC`,
          [head.session_id]
        );
      }

      // Hydrate memory summaries.
      const eventIds = Array.from(new Set(helpRows.map((row) => row.event_id as string).filter(Boolean)));
      const eventById = new Map<string, { content: string; event_type: string }>();
      if (eventIds.length > 0) {
        const rows = sqliteAll<{ id: string; content: string; event_type: string }>(
          this.db,
          `SELECT id, content, event_type FROM events WHERE id IN (${eventIds.map(() => '?').join(',')})`,
          eventIds
        );
        for (const row of rows) eventById.set(row.id, row);
      }

      const memories = helpRows.map((row) => {
        const event = eventById.get(row.event_id as string);
        let evidence: EvidenceMatch[] = [];
        try {
          const parsed = JSON.parse((row.evidence_json as string) || '[]');
          if (Array.isArray(parsed)) evidence = parsed;
        } catch { /* legacy/corrupt rows have no evidence */ }
        const summary = event
          ? event.content.substring(0, 240) + (event.content.length > 240 ? '…' : '')
          : '(event no longer available)';
        return {
          eventId: row.event_id as string,
          eventType: event?.event_type ?? null,
          summary,
          retrievalScore: (row.retrieval_score as number) ?? 0,
          helpfulnessScore: row.measured_at ? ((row.helpfulness_score as number) ?? null) : null,
          contentOverlapScore: (row.content_overlap_score as number) ?? null,
          evidence,
          measuredAt: (row.measured_at as string) ?? null,
          source: (row.source as string) || 'user_prompt'
        };
      });

      if (kind === 'query' && trace) {
        entries.push({
          traceId: headId,
          kind,
          sessionId: (trace.session_id as string) ?? null,
          question: (trace.raw_query_text as string) || (trace.query_text as string) || '',
          queryText: (trace.query_text as string) ?? null,
          strategy: (trace.strategy as string) ?? null,
          confidence: (trace.confidence as string) ?? null,
          candidateCount: (trace.candidate_count as number) ?? 0,
          selectedCount: (trace.selected_count as number) ?? 0,
          createdAt: toDateFromSQLite(trace.created_at as string),
          memories
        });
      } else {
        entries.push({
          traceId: headId.startsWith('ss-') ? null : headId,
          kind: 'session_start',
          sessionId: (head.session_id as string) ?? null,
          question: 'Session start — recent project context injected',
          queryText: null,
          strategy: 'session-start',
          confidence: null,
          candidateCount: memories.length,
          selectedCount: memories.length,
          createdAt: toDateFromSQLite(head.created_at as string),
          memories
        });
      }
    }

    return entries;
  }

  /**
   * Fast keyword search using FTS5
   * Returns events matching the search query, ranked by relevance
   */
  async keywordSearch(query: string, limit: number = 10): Promise<Array<{event: MemoryEvent; rank: number}>> {
    await this.initialize();

    // Escape special FTS5 characters and prepare search terms
    const searchTerms = query
      .replace(/['"(){}[\]^~*?:\\/-]/g, ' ')  // Remove special chars
      .split(/\s+/)
      .filter(term => term.length > 1)  // Filter short terms
      .map(term => `"${term}"*`)  // Prefix matching
      .join(' OR ');

    if (!searchTerms) {
      return [];
    }

    try {
      const rows = sqliteAll<Record<string, unknown>>(
        this.db,
        `SELECT e.*, fts.rank
         FROM events_fts fts
         JOIN events e ON e.id = fts.event_id
         WHERE events_fts MATCH ?
           AND ${notActiveQuarantinedSql('e.metadata')}
         ORDER BY fts.rank
         LIMIT ?`,
        [searchTerms, limit]
      );

      return rows.map(row => ({
        event: this.rowToEvent(row),
        rank: row.rank as number
      }));
    } catch (error: any) {
      // FTS table might not exist yet (old database)
      // Fallback to LIKE search
      const likePattern = `%${query}%`;
      const rows = sqliteAll<Record<string, unknown>>(
        this.db,
        `SELECT *, 0 as rank FROM events
         WHERE content LIKE ?
           AND ${notActiveQuarantinedSql()}
         ORDER BY timestamp DESC
         LIMIT ?`,
        [likePattern, limit]
      );

      return rows.map(row => ({
        event: this.rowToEvent(row),
        rank: 0
      }));
    }
  }

  /**
   * Rebuild FTS index from existing events
   * Call this once after upgrading to FTS5
   */
  /**
   * Drop and recreate the events_fts virtual table + sync triggers, repopulating
   * from the current events table. Recreating the table (instead of issuing
   * DELETEs against it) sidesteps a quirk where older migrated FTS5 tables fail
   * with `no such column: T.event_id` on synthetic deletes. Safe to call inside
   * a transaction (pure DDL + INSERT...SELECT, no transaction control).
   */
  private recreateEventsFtsTable(): void {
    sqliteExec(this.db, `
      DROP TRIGGER IF EXISTS events_fts_insert;
      DROP TRIGGER IF EXISTS events_fts_delete;
      DROP TRIGGER IF EXISTS events_fts_update;
      DROP TABLE IF EXISTS events_fts;

      CREATE VIRTUAL TABLE events_fts USING fts5(
        content,
        event_id UNINDEXED,
        tokenize='porter unicode61'
      );

      INSERT INTO events_fts(rowid, content, event_id)
      SELECT rowid, content, id FROM events;

      CREATE TRIGGER events_fts_insert AFTER INSERT ON events BEGIN
        INSERT INTO events_fts(rowid, content, event_id) VALUES (NEW.rowid, NEW.content, NEW.id);
      END;

      CREATE TRIGGER events_fts_delete AFTER DELETE ON events BEGIN
        DELETE FROM events_fts WHERE rowid = OLD.rowid;
      END;

      CREATE TRIGGER events_fts_update AFTER UPDATE ON events BEGIN
        DELETE FROM events_fts WHERE rowid = OLD.rowid;
        INSERT INTO events_fts(rowid, content, event_id) VALUES (NEW.rowid, NEW.content, NEW.id);
      END;
    `);
  }

  async rebuildFtsIndex(): Promise<number> {
    await this.initialize();

    // Get count of events to index
    const countRow = sqliteGet<{count: number}>(this.db, 'SELECT COUNT(*) as count FROM events', []);
    const totalEvents = countRow?.count ?? 0;

    this.recreateEventsFtsTable();

    return totalEvents;
  }

  /**
   * Get database instance for direct access
   */
  getDatabase(): SQLiteDatabase {
    return this.db;
  }

  private hasTableColumn(tableName: string, columnName: string): boolean {
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(tableName)) return false;
    try {
      const rows = sqliteAll<{ name: string }>(this.db, `PRAGMA table_info("${tableName}")`, []);
      return rows.some((row) => row.name === columnName);
    } catch {
      return false;
    }
  }

  private hasTable(tableName: string): boolean {
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(tableName)) return false;
    try {
      const rows = sqliteAll<{ name: string }>(
        this.db,
        `SELECT name FROM sqlite_master WHERE type = 'table' AND name = ? LIMIT 1`,
        [tableName]
      );
      return rows.length > 0;
    } catch {
      return false;
    }
  }

  /**
   * Add a column only when its table exists and the column is missing.
   *
   * Unlike a blind `try { ALTER } catch {}`, this distinguishes "already
   * migrated" / "table not present yet" (both safely skipped) from a genuine
   * failure (disk full, corruption), which is allowed to surface instead of
   * being silently swallowed as if the migration had succeeded.
   */
  private addColumnIfMissing(table: string, column: string, definition: string): void {
    if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(table) || !/^[A-Za-z_][A-Za-z0-9_]*$/.test(column)) {
      throw new Error(`Invalid identifier in migration: ${table}.${column}`);
    }
    if (!this.hasTable(table)) return;
    if (this.hasTableColumn(table, column)) return;
    sqliteExec(this.db, `ALTER TABLE ${table} ADD COLUMN ${column} ${definition};`);
  }


  async recordRetrievalTrace(input: {
    traceId?: string;
    sessionId?: string;
    projectHash?: string;
    queryText: string;
    rawQueryText?: string;
    queryRewriteKind?: string;
    strategy?: string;
    candidateEventIds: string[];
    selectedEventIds: string[];
    candidateDetails?: Array<{
      eventId: string;
      score: number;
      semanticScore?: number;
      lexicalScore?: number;
      recencyScore?: number;
      lanes?: RetrievalDebugLane[];
    }>;
    selectedDetails?: Array<{
      eventId: string;
      score: number;
      semanticScore?: number;
      lexicalScore?: number;
      recencyScore?: number;
      lanes?: RetrievalDebugLane[];
    }>;
    confidence?: string;
    fallbackTrace?: string[];
  }): Promise<void> {
    await this.initialize();

    const traceId = input.traceId || randomUUID();
    const queryRewriteKind = normalizeQueryRewriteKind(input.queryRewriteKind);
    const candidateDetails = normalizeRetrievalTraceDetails(input.candidateDetails);
    const selectedDetails = normalizeRetrievalTraceDetails(input.selectedDetails);
    sqliteRun(
      this.db,
      `INSERT INTO retrieval_traces (
        trace_id, session_id, project_hash, query_text, raw_query_text, query_rewrite_kind, strategy,
        candidate_event_ids, selected_event_ids, candidate_details_json, selected_details_json,
        candidate_count, selected_count, confidence, fallback_trace
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        traceId,
        input.sessionId || null,
        input.projectHash || null,
        input.queryText,
        input.rawQueryText || null,
        queryRewriteKind,
        input.strategy || null,
        JSON.stringify(input.candidateEventIds || []),
        JSON.stringify(input.selectedEventIds || []),
        JSON.stringify(candidateDetails),
        JSON.stringify(selectedDetails),
        (input.candidateEventIds || []).length,
        (input.selectedEventIds || []).length,
        input.confidence || null,
        JSON.stringify(input.fallbackTrace || [])
      ]
    );
  }

  async getRecentRetrievalTraces(limit: number = 50): Promise<Array<{
    traceId: string;
    sessionId?: string;
    projectHash?: string;
    queryText: string;
    rawQueryText?: string;
    queryRewriteKind?: string;
    strategy?: string;
    candidateEventIds: string[];
    selectedEventIds: string[];
    candidateDetails: Array<{
      eventId: string;
      score: number;
      semanticScore?: number;
      lexicalScore?: number;
      recencyScore?: number;
      lanes?: RetrievalDebugLane[];
    }>;
    selectedDetails: Array<{
      eventId: string;
      score: number;
      semanticScore?: number;
      lexicalScore?: number;
      recencyScore?: number;
      lanes?: RetrievalDebugLane[];
    }>;
    candidateCount: number;
    selectedCount: number;
    confidence?: string;
    fallbackTrace: string[];
    createdAt: Date;
  }>> {
    await this.initialize();

    try {
      const rows = sqliteAll<Record<string, unknown>>(
        this.db,
        `SELECT * FROM retrieval_traces ORDER BY created_at DESC LIMIT ?`,
        [limit]
      );

      return rows.map((row) => ({
        traceId: row.trace_id as string,
        sessionId: (row.session_id as string) || undefined,
        projectHash: (row.project_hash as string) || undefined,
        queryText: row.query_text as string,
        rawQueryText: (row.raw_query_text as string) || undefined,
        queryRewriteKind: normalizeQueryRewriteKind(row.query_rewrite_kind as string | null),
        strategy: (row.strategy as string) || undefined,
        candidateEventIds: row.candidate_event_ids ? JSON.parse(row.candidate_event_ids as string) : [],
        selectedEventIds: row.selected_event_ids ? JSON.parse(row.selected_event_ids as string) : [],
        candidateDetails: parseRetrievalTraceDetails(row.candidate_details_json),
        selectedDetails: parseRetrievalTraceDetails(row.selected_details_json),
        candidateCount: Number(row.candidate_count || 0),
        selectedCount: Number(row.selected_count || 0),
        confidence: (row.confidence as string) || undefined,
        fallbackTrace: row.fallback_trace ? JSON.parse(row.fallback_trace as string) : [],
        createdAt: toDateFromSQLite(row.created_at),
      }));
    } catch (err: any) {
      if (err?.message?.includes('no such table')) return [];
      throw err;
    }
  }

  async getRetrievalTraceStats(): Promise<{
    totalQueries: number;
    avgCandidateCount: number;
    avgSelectedCount: number;
    selectionRate: number;
    rewrittenQueries: number;
    rewriteRate: number;
    rewrittenQueriesWithSelection: number;
    rawQueriesWithSelection: number;
    rewrittenSelectionRate: number;
    rawSelectionRate: number;
    avgSelectedCountForRewrittenQueries: number;
    avgSelectedCountForRawQueries: number;
    strategyBreakdown: Array<{
      strategy: string;
      totalQueries: number;
      queriesWithSelection: number;
      rewrittenQueries: number;
      rewriteRate: number;
      totalCandidateCount: number;
      totalSelectedCount: number;
      avgCandidateCount: number;
      avgSelectedCount: number;
      selectionRate: number;
      queryYieldRate: number;
    }>;
  }> {
    await this.initialize();

    try {
      const rewrittenQueryRewriteKindSql = this.hasTableColumn('retrieval_traces', 'query_rewrite_kind')
        ? REWRITTEN_QUERY_REWRITE_KIND_SQL
        : '0';
      const row = sqliteGet<Record<string, unknown>>(
        this.db,
        `SELECT
          COUNT(*) as total_queries,
          AVG(candidate_count) as avg_candidate_count,
          AVG(selected_count) as avg_selected_count,
          SUM(CASE WHEN ${rewrittenQueryRewriteKindSql} THEN 1 ELSE 0 END) as rewritten_queries,
          SUM(CASE WHEN ${rewrittenQueryRewriteKindSql} AND selected_count > 0 THEN 1 ELSE 0 END) as rewritten_queries_with_selection,
          SUM(CASE WHEN NOT (${rewrittenQueryRewriteKindSql}) AND selected_count > 0 THEN 1 ELSE 0 END) as raw_queries_with_selection,
          AVG(CASE WHEN ${rewrittenQueryRewriteKindSql} THEN selected_count END) as avg_selected_count_for_rewritten_queries,
          AVG(CASE WHEN NOT (${rewrittenQueryRewriteKindSql}) THEN selected_count END) as avg_selected_count_for_raw_queries,
          CASE
            WHEN SUM(candidate_count) > 0 THEN (SUM(selected_count) * 1.0 / SUM(candidate_count))
            ELSE 0
          END as selection_rate
         FROM retrieval_traces`,
        []
      );

      const strategyColumnSql = this.hasTableColumn('retrieval_traces', 'strategy')
        ? "COALESCE(NULLIF(TRIM(strategy), ''), 'unknown')"
        : "'unknown'";
      const strategyRows = sqliteAll<Record<string, unknown>>(
        this.db,
        `SELECT
          ${strategyColumnSql} as strategy,
          COUNT(*) as total_queries,
          SUM(CASE WHEN selected_count > 0 THEN 1 ELSE 0 END) as queries_with_selection,
          SUM(CASE WHEN ${rewrittenQueryRewriteKindSql} THEN 1 ELSE 0 END) as rewritten_queries,
          SUM(candidate_count) as total_candidate_count,
          SUM(selected_count) as total_selected_count,
          AVG(candidate_count) as avg_candidate_count,
          AVG(selected_count) as avg_selected_count,
          CASE
            WHEN SUM(candidate_count) > 0 THEN (SUM(selected_count) * 1.0 / SUM(candidate_count))
            ELSE 0
          END as selection_rate
         FROM retrieval_traces
         GROUP BY ${strategyColumnSql}
         ORDER BY total_queries DESC, strategy ASC`,
        []
      );

      const strategyBreakdown = strategyRows.map((strategyRow) => {
        const strategyTotalQueries = Number(strategyRow.total_queries || 0);
        const strategyQueriesWithSelection = Number(strategyRow.queries_with_selection || 0);
        const strategyRewrittenQueries = Number(strategyRow.rewritten_queries || 0);
        return {
          strategy: String(strategyRow.strategy || 'unknown'),
          totalQueries: strategyTotalQueries,
          queriesWithSelection: strategyQueriesWithSelection,
          rewrittenQueries: strategyRewrittenQueries,
          rewriteRate: strategyTotalQueries > 0 ? strategyRewrittenQueries / strategyTotalQueries : 0,
          totalCandidateCount: Number(strategyRow.total_candidate_count || 0),
          totalSelectedCount: Number(strategyRow.total_selected_count || 0),
          avgCandidateCount: Number(strategyRow.avg_candidate_count || 0),
          avgSelectedCount: Number(strategyRow.avg_selected_count || 0),
          selectionRate: Number(strategyRow.selection_rate || 0),
          queryYieldRate: strategyTotalQueries > 0 ? strategyQueriesWithSelection / strategyTotalQueries : 0,
        };
      });

      const totalQueries = Number(row?.total_queries || 0);
      const rewrittenQueries = Number(row?.rewritten_queries || 0);
      const rawQueries = Math.max(0, totalQueries - rewrittenQueries);
      const rewrittenQueriesWithSelection = Number(row?.rewritten_queries_with_selection || 0);
      const rawQueriesWithSelection = Number(row?.raw_queries_with_selection || 0);

      return {
        totalQueries,
        avgCandidateCount: Number(row?.avg_candidate_count || 0),
        avgSelectedCount: Number(row?.avg_selected_count || 0),
        selectionRate: Number(row?.selection_rate || 0),
        rewrittenQueries,
        rewriteRate: totalQueries > 0 ? rewrittenQueries / totalQueries : 0,
        rewrittenQueriesWithSelection,
        rawQueriesWithSelection,
        rewrittenSelectionRate: rewrittenQueries > 0 ? rewrittenQueriesWithSelection / rewrittenQueries : 0,
        rawSelectionRate: rawQueries > 0 ? rawQueriesWithSelection / rawQueries : 0,
        avgSelectedCountForRewrittenQueries: Number(row?.avg_selected_count_for_rewritten_queries || 0),
        avgSelectedCountForRawQueries: Number(row?.avg_selected_count_for_raw_queries || 0),
        strategyBreakdown,
      };
    } catch (err: any) {
      if (err?.message?.includes('no such table')) {
        return {
          totalQueries: 0,
          avgCandidateCount: 0,
          avgSelectedCount: 0,
          selectionRate: 0,
          rewrittenQueries: 0,
          rewriteRate: 0,
          rewrittenQueriesWithSelection: 0,
          rawQueriesWithSelection: 0,
          rewrittenSelectionRate: 0,
          rawSelectionRate: 0,
          avgSelectedCountForRewrittenQueries: 0,
          avgSelectedCountForRawQueries: 0,
          strategyBreakdown: [],
        };
      }
      throw err;
    }
  }

  /**
   * Close database connection
   */
  async close(): Promise<void> {
    sqliteClose(this.db);
  }

  /**
   * Get events grouped by turn_id for a session
   * Returns turns ordered by first event timestamp (newest first)
   */
  async getSessionTurns(sessionId: string, options?: { limit?: number; offset?: number } & QuarantineReadOptions): Promise<Array<{
    turnId: string;
    events: MemoryEvent[];
    startedAt: Date;
    promptPreview: string;
    eventCount: number;
    toolCount: number;
    hasResponse: boolean;
  }>> {
    await this.initialize();

    const limit = options?.limit || 20;
    const offset = options?.offset || 0;

    // Get distinct turn_ids for this session, ordered by first event timestamp
    const turnRows = sqliteAll<{ turn_id: string; min_ts: string }>(
      this.db,
      `SELECT turn_id, MIN(timestamp) as min_ts
       FROM events
       WHERE session_id = ? AND turn_id IS NOT NULL
         AND ${maybeQuarantinePredicate(options)}
       GROUP BY turn_id
       ORDER BY min_ts DESC
       LIMIT ? OFFSET ?`,
      [sessionId, limit, offset]
    );

    const turns: Array<{
      turnId: string;
      events: MemoryEvent[];
      startedAt: Date;
      promptPreview: string;
      eventCount: number;
      toolCount: number;
      hasResponse: boolean;
    }> = [];

    for (const turnRow of turnRows) {
      const events = await this.getEventsByTurn(turnRow.turn_id, options);

      const promptEvent = events.find(e => e.eventType === 'user_prompt');
      const toolEvents = events.filter(e => e.eventType === 'tool_observation');
      const hasResponse = events.some(e => e.eventType === 'agent_response');

      turns.push({
        turnId: turnRow.turn_id,
        events,
        startedAt: toDateFromSQLite(turnRow.min_ts),
        promptPreview: promptEvent
          ? promptEvent.content.slice(0, 200) + (promptEvent.content.length > 200 ? '...' : '')
          : '(no prompt)',
        eventCount: events.length,
        toolCount: toolEvents.length,
        hasResponse
      });
    }

    return turns;
  }

  /**
   * Get all events for a specific turn_id
   */
  async getEventsByTurn(turnId: string, options?: QuarantineReadOptions): Promise<MemoryEvent[]> {
    await this.initialize();

    const rows = sqliteAll<Record<string, unknown>>(
      this.db,
      `SELECT * FROM events WHERE turn_id = ? AND ${maybeQuarantinePredicate(options)} ORDER BY timestamp ASC`,
      [turnId]
    );

    return rows.map(this.rowToEvent);
  }

  /**
   * Count total turns for a session
   */
  async countSessionTurns(sessionId: string, options?: QuarantineReadOptions): Promise<number> {
    await this.initialize();

    const row = sqliteGet<{ count: number }>(
      this.db,
      `SELECT COUNT(DISTINCT turn_id) as count
       FROM events
       WHERE session_id = ? AND turn_id IS NOT NULL
         AND ${maybeQuarantinePredicate(options)}`,
      [sessionId]
    );

    return row?.count || 0;
  }

  /**
   * Migrate existing events: backfill turn_id for events that have turnId in metadata
   * but no turn_id column value (for events stored before this migration)
   */
  async backfillTurnIds(): Promise<number> {
    await this.initialize();

    // Find events with turnId in metadata JSON but no turn_id column value
    const rows = sqliteAll<{ id: string; metadata: string }>(
      this.db,
      `SELECT id, metadata FROM events
       WHERE turn_id IS NULL AND metadata IS NOT NULL AND metadata LIKE '%turnId%'`
    );

    let updated = 0;
    for (const row of rows) {
      try {
        const metadata = JSON.parse(row.metadata);
        if (metadata.turnId) {
          sqliteRun(
            this.db,
            `UPDATE events SET turn_id = ? WHERE id = ?`,
            [metadata.turnId, row.id]
          );
          updated++;
        }
      } catch {
        // Skip rows with invalid JSON
      }
    }

    return updated;
  }

  /**
   * Delete all events for a session (for force reimport)
   */
  async deleteSessionEvents(sessionId: string): Promise<number> {
    await this.initialize();

    // Get event IDs first for cascading deletes
    const events = sqliteAll<{ id: string }>(
      this.db,
      `SELECT id FROM events WHERE session_id = ?`,
      [sessionId]
    );

    if (events.length === 0) return 0;

    const eventIds = events.map(e => e.id);
    const placeholders = eventIds.map(() => '?').join(',');

    // Run the trigger-drop -> delete -> FTS-rebuild -> trigger-recreate sequence
    // atomically. Outside a transaction, a crash or error after the triggers are
    // dropped would permanently desync the FTS index, so keyword search would
    // silently return stale results forever. Inside a transaction any failure
    // rolls back and the triggers/index stay consistent. We pre-check optional
    // table existence (instead of swallowing per-statement errors) so a genuine
    // failure is never mistaken for a benign "table does not exist".
    // Only the tables that both exist and key cascades on event_id. (vector_outbox
    // keys on item_id, not event_id; the previous code swallowed the resulting
    // error, so it was never cleaned here either — behavior preserved.)
    const relatedTables = ['event_dedup', 'memory_levels', 'embedding_queue', 'embedding_outbox', 'vector_outbox', 'event_citations']
      .filter((table) => this.hasTable(table) && this.hasTableColumn(table, 'event_id'));

    const runDelete = this.db.transaction((): number => {
      // Drop FTS sync triggers before the bulk delete so the synthetic FTS
      // delete (which can fail with `no such column: T.event_id` on older
      // migrated tables, and risks SQLITE_CORRUPT_VTAB) never fires mid-delete.
      for (const triggerName of ['events_fts_delete', 'events_fts_update', 'events_fts_insert']) {
        sqliteRun(this.db, `DROP TRIGGER IF EXISTS ${triggerName}`);
      }

      // Delete from related tables first.
      for (const table of relatedTables) {
        sqliteRun(this.db, `DELETE FROM ${table} WHERE event_id IN (${placeholders})`, eventIds);
      }

      // Delete events.
      const result = sqliteRun(this.db, `DELETE FROM events WHERE session_id = ?`, [sessionId]);

      // Recreate the FTS table + triggers from the remaining events. A failure
      // here aborts the whole transaction rather than leaving the index desynced
      // with its triggers permanently dropped.
      this.recreateEventsFtsTable();

      return result.changes || 0;
    });

    return runDelete();
  }

  /**
   * Convert database row to MemoryEvent
   */
  private rowToEvent(row: Record<string, unknown>): MemoryEvent {
    const event: any = {
      id: row.id as string,
      eventType: row.event_type as 'user_prompt' | 'agent_response' | 'session_summary',
      sessionId: row.session_id as string,
      timestamp: toDateFromSQLite(row.timestamp),
      content: row.content as string,
      canonicalKey: row.canonical_key as string,
      dedupeKey: row.dedupe_key as string,
      metadata: safeParseMetadataValue(row.metadata)
    };

    // Include access tracking fields if present
    if (row.access_count !== undefined) {
      event.access_count = row.access_count;
    }
    if (row.last_accessed_at !== undefined) {
      event.last_accessed_at = row.last_accessed_at;
    }
    // Include turn_id if present
    if (row.turn_id !== undefined && row.turn_id !== null) {
      event.turn_id = row.turn_id;
    }

    return event;
  }
}
